import React, { useState, useEffect } from 'react';
import { Form, Container, Row, Col, Button, Alert, Spinner, Image } from 'react-bootstrap';
import C from '../lib/Config';
import swal from 'sweetalert';
import { FaPencilAlt } from 'react-icons/fa';

const MedicalHistory = (props) => {
  // Add custom styles for tables
  React.useEffect(() => {
    // Create a style element
    const styleElement = document.createElement('style');
    // Append the style element to the document head
    document.head.appendChild(styleElement);
    
    // Clean up function to remove the style element when component unmounts
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Initialize component with patientId from props or URL parameters
  React.useEffect(() => {
    // Check if patientId is passed as a prop
    let id = props.patientId;
    
    // If not found in props, try to get from URL query parameters
    if (!id && window.location.search) {
      const searchParams = new URLSearchParams(window.location.search);
      id = searchParams.get('patientId');
    }
    
    // If still not found, check sessionStorage (from CaseProfile link)
    if (!id) {
      id = sessionStorage.getItem('currentPatientId');
    }
    
    // If we have a patientId, set it and load data
    if (id) {
      setPatientId(id);
      // Initialize patient data
      fetchPatientSummaryDetails(id);
      fetchAllMedicalHistory(id);
      // Also set the patientId in the patientData object
      setPatientData(prevData => ({
        ...prevData,
        patientId: id
      }));
      
      // Load all the options for dropdowns
      fetchSpecificationOptions();
      fetchRelationOptions();
      fetchAllergyOptions();
      fetchAddictionTypeOptions();
      fetchAllergyTypeOptions();
      fetchDurationOptions();
      fetchMedicineOptions();
      fetchDoseOptions();
      fetchWhenOptions();
      fetchComorbidityOptions();
      
      // Default to view mode when component is initialized
      setShowEditForm(false);
    }
  }, [props.patientId]);

  // State initialization with useState hooks
  const [patientId, setPatientId] = useState(props.patientId || '');
  const [patientData, setPatientData] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    age: '',
    contact: '',
    patientId: '',
    status: '',
    consultant: '',
    currentAddress: '',
    referral: '',
    allergies: '',
    comorbidities: '',
    biomarker: '',
    dob: ''
  });
  
  // Medical history state
  const [addictionHistory, setAddictionHistory] = useState('');
  const [allergiesHistory, setAllergiesHistory] = useState('');
  const [familyHistory, setFamilyHistory] = useState('');
  const [currentMedication, setCurrentMedication] = useState('');
  const [pastMedicalHistory, setPastMedicalHistory] = useState('');
  const [comorbidities, setComorbidities] = useState('');
  const [pastSurgicalHistory, setPastSurgicalHistory] = useState('');
  const [pastOncologyHistory, setPastOncologyHistory] = useState('');
  const [patientGender, setPatientGender] = useState('');
  
  // Reproductive history state
  const [menarche, setMenarche] = useState('');
  const [periods, setPeriods] = useState('');
  const [bleeding, setBleeding] = useState('');
  const [menopausalStatus, setMenopausalStatus] = useState('');
  const [maritalStatus, setMaritalStatus] = useState('');
  const [children, setChildren] = useState('');
  const [numberOfChildren, setNumberOfChildren] = useState('');
  const [ageAtFirstBirth, setAgeAtFirstBirth] = useState('');
  const [breastFeed, setBreastFeed] = useState('');
  
  // Tables data state
  const [addictionTable, setAddictionTable] = useState([{ 
    historyOfAddiction: '', startedAtAge: '', perDay: '', duration: '', reformade: '', comments: '' 
  }]);
  const [allergiesTable, setAllergiesTable] = useState([{ 
    allergy: '', allergyType: '', duration: '', comment: '' 
  }]);
  const [familyTable, setFamilyTable] = useState([{ 
    historyOf: '', typeOfRelation: '', atTheAgeOf: '', specification: '', comment: '' 
  }]);
  const [medicationTable, setMedicationTable] = useState([{ 
    medicine: '', dose: '', when: '', duration: '', instructions: '' 
  }]);
  const [comorbidityTable, setComorbidityTable] = useState([{ 
    comorbidity: '', duration: '', onMedication: '' 
  }]);
  const [fetchedSpecificationOptions, setFetchedSpecificationOptions] = useState([]);
  const [newSpecificationOption, setNewSpecificationOption] = useState('');
  const [fetchedRelationOptions, setFetchedRelationOptions] = useState([]);
  const [newRelationOption, setNewRelationOption] = useState('');
  const [fetchedAllergyOptions, setFetchedAllergyOptions] = useState([]);
  const [newAllergyOption, setNewAllergyOption] = useState('');
  const [fetchedAddictionTypeOptions, setFetchedAddictionTypeOptions] = useState([]);
  const [fetchedAddictionTypeMasterData, setFetchedAddictionTypeMasterData] = useState([]);
  const [newAddictionTypeOption, setNewAddictionTypeOption] = useState('');
  const [fetchedAllergyTypeOptions, setFetchedAllergyTypeOptions] = useState([]);
  const [newAllergyTypeOption, setNewAllergyTypeOption] = useState('');
  const [fetchedDurationOptions, setFetchedDurationOptions] = useState([]);
  const [fetchedMedicineOptions, setFetchedMedicineOptions] = useState([]);
  const [newMedicineOption, setNewMedicineOption] = useState('');
  const [fetchedDoseOptions, setFetchedDoseOptions] = useState([]);
  const [newDoseOption, setNewDoseOption] = useState('');
  const [fetchedWhenOptions, setFetchedWhenOptions] = useState([]);
  const [newWhenOption, setNewWhenOption] = useState('');
  const [fetchedComorbidityOptions, setFetchedComorbidityOptions] = useState([]);
  const [newComorbidityOption, setNewComorbidityOption] = useState('');

  // Additional state
  const [pastMedicalHistoryDetails, setPastMedicalHistoryDetails] = useState('');
  const [pastSurgicalHistoryDetails, setPastSurgicalHistoryDetails] = useState('');
  const [pastOncologyHistoryDetails, setPastOncologyHistoryDetails] = useState('');
  const [saveLoader, setSaveLoader] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [errors, setErrors] = useState({});

  // Function to toggle edit form visibility
  const toggleEditForm = () => {
    setShowEditForm(!showEditForm);
  };

  // Track deleted rows to ensure they don't reappear after refresh
  const [deletedRowsTracking, setDeletedRowsTracking] = useState({
    deletedAddictionTable: [],
    deletedAllergiesTable: [],
    deletedFamilyTable: [],
    deletedMedicationTable: [],
    deletedComorbidityTable: []
  });
  
  // Options data - could be moved outside component if they don't change
  const allergyOptions = ['Live attenuated virus antigen', 'Isomatrose', 'Germanium bromide', 'Benzenesulfonic acid', 'Pazolenant', 'Inclifran', 'Monkeypox virus DNA'];
  const allergyTypeOptions = ['Food', 'Drug', 'Environmental', 'Insect', 'Latex', 'Mold', 'Pet', 'Pollen'];
  const durationOptions = ['_Day', '_Week', '_Month', '_Year'];
  const relationTypeOptions = ['1st', '2nd', '3rd', 'Distant'];
  const relationSpecificationOptions = ['Maternal', 'Paternal'];
  const whenOptions = ['Before Food', 'After Food', 'Before Breakfast', 'After Breakfast', 'Before Lunch', 'After Lunch', 'Before Dinner', 'After Dinner', 'Once', 'Twice'];
  const doseOptions = ['0-0-1', '0-1-0', '1-0-0', '1-1-0', '1-0-1', '1-1-1'];
  const addictionOptions = ['Alcohol', 'Mava', 'Jarda', 'Hookah', 'Drugs', 'Others', 'No History of Addictions'];
  const comorbiditiesOptions = [
    'Chronic nontraumatic intracranial subdural hematoma', 'Adenosine deaminase 2 deficiency', 
    'Malignant melanoma of skin of left wrist', 'Renal scarring due to vesicoureteral reflux', 'Venous ulcer of toe of left foot'
  ];

  // Add this function to fetch patient summary details
  const fetchPatientSummaryDetails = async (patientId) => {
    if (!patientId) return;

    try {
      console.log(`Fetching patient details for ID: ${patientId}`);
      const response = await fetch(`${C.getUrl()}/userCase/getPatientSummaryDetails?patientId=${patientId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        console.warn(`Failed to fetch patient summary. Status: ${response.status}`);
        return;
      }
      
      // Get the raw response text first to inspect it
      const responseText = await response.text();
      console.log('Raw API response:', responseText);
      
      let responseData;
      try {
        // Try to parse the response as JSON
        responseData = JSON.parse(responseText);
      } catch (jsonError) {
        console.error('Error parsing API response as JSON:', jsonError);
        return;
      }
      
      if (responseData && responseData.statusCode === 200) {
        try {
          // Parse the message field which contains the actual data
          const patientDetails = JSON.parse(responseData.message);
          console.log('Parsed patient details:', patientDetails);
          
          // Log the specific fields we're interested in
          console.log('Patient DTO:', patientDetails.patientDetailsDto);
          console.log('Gender data:', patientDetails.patientDetailsDto?.genderMstDto);
          console.log('Age data:', patientDetails.patientDetailsDto?.age);
          
          // Extract all required patient information with careful path checking
          const patientDetailsDto = patientDetails.patientDetailsDto || {};
          
          // Check for gender in multiple possible locations and convert to full form
          let gender = '';
          if (patientDetailsDto.genderMstDto && patientDetailsDto.genderMstDto.gender) {
            const genderCode = patientDetailsDto.genderMstDto.gender;
            gender = convertGenderToFullForm(genderCode);
          } else if (patientDetailsDto.gender) {
            gender = convertGenderToFullForm(patientDetailsDto.gender);
          }
          
          // Check for age in multiple possible formats
          let age = '';
          if (patientDetailsDto.age) {
            age = patientDetailsDto.age;
          } else if (patientDetailsDto.dob) {
            // Calculate age from DOB if available
            const dob = new Date(patientDetailsDto.dob);
            const today = new Date();
            age = today.getFullYear() - dob.getFullYear();
          }
          
          // Get the case number (which might be different from patientId)
          // Look for all possible field names that might contain the case number
          const caseNo = patientDetailsDto.caseNo || 
                        patientDetailsDto.caseNumber || 
                        patientDetailsDto.caseId || 
                        patientDetailsDto.case_no || 
                        patientDetailsDto.case_number || 
                        patientDetailsDto.case_id || 
                        '';
          
          const patientInfo = {
            firstName: patientDetailsDto.firstName || '',
            lastName: patientDetailsDto.lastName || '',
            gender: gender,
            age: age,
            contact: patientDetailsDto.contactNumber || '',
            patientId: patientId,
            caseNo: caseNo, // Store the case number separately
            status: patientDetailsDto.status || '',
            consultant: patientDetailsDto.consultant || '',
            currentAddress: patientDetailsDto.currentAddress || '',
            referral: patientDetailsDto.referral || '',
            allergies: patientDetailsDto.allergies || '',
            comorbidities: patientDetailsDto.comorbidities || '',
            biomarker: patientDetailsDto.biomarker || '',
            dob: patientDetailsDto.dob || ''
          };
          
          console.log('Extracted patient info:', patientInfo);
          
          // Update the patientData state with all the information
          setPatientData(patientInfo);
          
          // Also update gender state for other parts of the component
          setPatientGender(gender);
          console.log('Updated patient data state with:', patientInfo);
        } catch (parseError) {
          console.error('Error parsing patient details:', parseError);
        }
      }
    } catch (error) {
      console.error('Error fetching patient summary details:', error);
    }
  };
  
  // Helper function to convert gender codes to full form
  const convertGenderToFullForm = (genderCode) => {
    if (!genderCode) return '';
    
    // Convert to uppercase for consistent comparison
    const code = genderCode.toUpperCase();
    
    switch (code) {
      case 'M':
        return 'Male';
      case 'F':
        return 'Female';
      case 'O':
        return 'Other';
      default:
        // If it's already in full form or unknown, return as is
        return genderCode;
    }
  };

  const fetchSpecificationOptions = async () => {
    try {
      const response = await fetch(`${C.getUrl()}/specificationMaster/getAllSpecifications`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        console.warn(`Failed to fetch specification options. Status: ${response.status}`);
        return;
      }
      
      const data = await response.json();
      
      if (data && data.statusCode === 200) {
        // Parse the JSON string in the message field
        const specificationsArray = JSON.parse(data.message || '[]');
        // Extract specification values
        const options = specificationsArray.map(item => item.specification);
        setFetchedSpecificationOptions(options);
      }
    } catch (error) {
      console.error('Error fetching specification options:', error);
    }
  };

  const saveNewSpecificationOption = async (newOption) => {
    try {
      const response = await fetch(`${C.getUrl()}/specificationMaster/create`, {
        method: 'PUT', // Using PUT as specified
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          specification: newOption,
          activeInd: "Y"
        }),
      });
      
      if (!response.ok) {
        console.warn(`Failed to save new specification option. Status: ${response.status}`);
        return false;
      }
      
      const data = await response.json();
      
      if (data && data.statusCode === 200) {
        // Refresh the options after saving a new one
        fetchSpecificationOptions();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error saving new specification option:', error);
      return false;
    }
  };

  const fetchRelationOptions = async () => {
    try {
      const response = await fetch(`${C.getUrl()}/relationMaster/getAllRelations`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        console.warn(`Failed to fetch relation options. Status: ${response.status}`);
        return;
      }
      
      const data = await response.json();
      
      if (data && data.statusCode === 200) {
        // Parse the JSON string in the message field
        const relationsArray = JSON.parse(data.message || '[]');
        // Extract relation values
        const options = relationsArray.map(item => item.relation);
        setFetchedRelationOptions(options);
      }
    } catch (error) {
      console.error('Error fetching relation options:', error);
    }
  };
  
  const saveNewRelationOption = async (newOption) => {
    try {
      const response = await fetch(`${C.getUrl()}/relationMaster/create`, {
        method: 'PUT', // Using PUT as specified
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          relation: newOption,
          activeInd: "Y"
        }),
      });
      
      if (!response.ok) {
        console.warn(`Failed to save new relation option. Status: ${response.status}`);
        return false;
      }
      
      const data = await response.json();
      
      if (data && data.statusCode === 200) {
        // Refresh the options after saving a new one
        fetchRelationOptions();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error saving new relation option:', error);
      return false;
    }
  };

  const fetchAllergyOptions = async () => {
    try {
      const response = await fetch(`${C.getUrl()}/medicalAllergiesMaster/getAllAllergies`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        console.warn(`Failed to fetch allergy options. Status: ${response.status}`);
        return;
      }
      
      const data = await response.json();
      
      if (data && data.statusCode === 200) {
        // Parse the JSON string in the message field
        const allergiesArray = JSON.parse(data.message || '[]');
        // Extract allergy values
        const options = allergiesArray.map(item => item.allergy);
        setFetchedAllergyOptions(options);
      }
    } catch (error) {
      console.error('Error fetching allergy options:', error);
    }
  };
  
  const saveNewAllergyOption = async (newOption) => {
    try {
      const response = await fetch(`${C.getUrl()}/medicalAllergiesMaster/create`, {
        method: 'PUT', // Using PUT as specified
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          allergy: newOption,
          activeInd: "Y"
        }),
      });
      
      if (!response.ok) {
        console.warn(`Failed to save new allergy option. Status: ${response.status}`);
        return false;
      }
      
      const data = await response.json();
      
      if (data && data.statusCode === 200) {
        // Refresh the options after saving a new one
        fetchAllergyOptions();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error saving new allergy option:', error);
      return false;
    }
  };

  const fetchAddictionTypeOptions = async () => {
    try {
      const response = await fetch(`${C.getUrl()}/addictionMaster/getAllAddictions`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        console.warn(`Failed to fetch addiction options. Status: ${response.status}`);
        return;
      }
      
      const data = await response.json();
      
      if (data && data.statusCode === 200) {
        // Parse the JSON string in the message field
        const addictionsArray = JSON.parse(data.message || '[]');
        // Store the full array for later use (to get IDs)
        setFetchedAddictionTypeMasterData(addictionsArray);
        // Extract addiction values for the dropdown
        const options = addictionsArray.map(item => item.addiction);
        setFetchedAddictionTypeOptions(options);
      }
    } catch (error) {
      console.error('Error fetching addiction options:', error);
    }
  };
  
  const saveNewAddictionTypeOption = async (newOption) => {
    try {
      const response = await fetch(`${C.getUrl()}/addictionMaster/create`, {
        method: 'PUT', // Using PUT as specified
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          addiction: newOption,
          activeInd: "Y"
        }),
      });
      
      if (!response.ok) {
        console.warn(`Failed to save new addiction option. Status: ${response.status}`);
        return false;
      }
      
      const data = await response.json();
      
      if (data && data.statusCode === 200) {
        // Refresh the options after saving a new one
        fetchAddictionTypeOptions();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error saving new addiction option:', error);
      return false;
    }
  };

  const fetchAllergyTypeOptions = async () => {
    try {
      const response = await fetch(`${C.getUrl()}/medicalAllergiesTypeMaster/getAllAllergiesTypes`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        console.warn(`Failed to fetch allergy type options. Status: ${response.status}`);
        return;
      }
      
      const data = await response.json();
      
      if (data && data.statusCode === 200) {
        // Parse the JSON string in the message field
        const allergyTypesArray = JSON.parse(data.message || '[]');
        // Extract allergy type values
        const options = allergyTypesArray.map(item => item.allergy);
        setFetchedAllergyTypeOptions(options);
      }
    } catch (error) {
      console.error('Error fetching allergy type options:', error);
    }
  };
  
  const saveNewAllergyTypeOption = async (newOption) => {
    try {
      const response = await fetch(`${C.getUrl()}/medicalAllergiesTypeMaster/create`, {
        method: 'PUT', // Using PUT as specified
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          allergy: newOption,
          activeInd: "Y"
        }),
      });
      
      if (!response.ok) {
        console.warn(`Failed to save new allergy type option. Status: ${response.status}`);
        return false;
      }
      
      const data = await response.json();
      
      if (data && data.statusCode === 200) {
        // Refresh the options after saving a new one
        fetchAllergyTypeOptions();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error saving new allergy type option:', error);
      return false;
    }
  };

  const fetchDurationOptions = async () => {
    try {
      const response = await fetch(`${C.getUrl()}/patientOpVisit/getAllDurationMaster`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        console.warn(`Failed to fetch duration options. Status: ${response.status}`);
        return;
      }
      
      const data = await response.json();
      
      if (data && data.statusCode === 200) {
        // Parse the JSON string in the message field and handle potential incomplete response
        let durationsArray = [];
        try {
          // The response message appears to be incomplete in your example, so we'll handle it carefully
          const messageStr = data.message || '[]';
          // If the message is incomplete JSON, we'll try to fix it or use an empty array
          durationsArray = JSON.parse(messageStr.endsWith('}]') ? messageStr : messageStr + '}]');
        } catch (e) {
          console.error('Error parsing duration data:', e);
          durationsArray = [];
        }
        
        // Extract duration values
        const options = durationsArray.map(item => item.durationName);
        setFetchedDurationOptions(options);
      }
    } catch (error) {
      console.error('Error fetching duration options:', error);
    }
  };

  const fetchMedicineOptions = async () => {
    try {
      console.log("Fetching medicine options...");
      const response = await fetch(`${C.getUrl()}/medicalMedicineMaster/getAllMedicines`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      // Log the raw response for debugging
      const responseText = await response.text();
      console.log("Medicine API response:", responseText);
      
      if (!response.ok) {
        console.error(`Failed to fetch medicine options. Status: ${response.status}`);
        return;
      }
      
      // Try to parse the response as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Error parsing response as JSON:", parseError);
        return;
      }
      
      if (data && data.statusCode === 200) {
        try {
          const messageStr = data.message || '[]';
          const medicinesArray = JSON.parse(messageStr);
          
          // Extract medicine values
          const options = medicinesArray.map(item => item.medicine);
          console.log("Extracted medicine options:", options);
          setFetchedMedicineOptions(options);
        } catch (messageParseError) {
          console.error("Error parsing message field:", messageParseError);
        }
      }
    } catch (error) {
      console.error('Error fetching medicine options:', error);
    }
  };
  
  const saveNewMedicineOption = async (newOption) => {
    try {
      const response = await fetch(`${C.getUrl()}/medicalMedicineMaster/create`, {
        method: 'PUT', // Using PUT as specified
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          medicine: newOption,
          activeInd: "Y"
        }),
      });
      
      if (!response.ok) {
        console.warn(`Failed to save new medicine option. Status: ${response.status}`);
        return false;
      }
      
      const data = await response.json();
      
      if (data && data.statusCode === 200) {
        // Refresh the options after saving a new one
        fetchMedicineOptions();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error saving new medicine option:', error);
      return false;
    }
  };

  const fetchDoseOptions = async () => {
    try {
      console.log("Fetching dose options...");
      const response = await fetch(`${C.getUrl()}/patientOpVisit/getAllDoseMaster`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      // Log the raw response for debugging
      const responseText = await response.text();
      console.log("Dose API response:", responseText);
      
      if (!response.ok) {
        console.error(`Failed to fetch dose options. Status: ${response.status}`);
        return;
      }
      
      // Try to parse the response as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Error parsing response as JSON:", parseError);
        return;
      }
      
      if (data && data.statusCode === 200) {
        try {
          const messageStr = data.message || '[]';
          
          // The message appears to be incomplete in your example, so handle it carefully
          let doseArray = [];
          try {
            // If the message is incomplete JSON, try to fix it or use an empty array
            doseArray = JSON.parse(messageStr.endsWith('}]') ? messageStr : messageStr + '}]');
          } catch (e) {
            console.error('Error parsing dose data:', e);
            doseArray = [];
          }
          
          // Extract dose values - assuming 'doseName' is the field we want
          const options = doseArray.map(item => item.doseName || '').filter(Boolean);
          console.log("Extracted dose options:", options);
          
          if (options.length > 0) {
            setFetchedDoseOptions(options);
          }
        } catch (messageParseError) {
          console.error("Error parsing message field:", messageParseError);
        }
      }
    } catch (error) {
      console.error('Error fetching dose options:', error);
    }
  };
  
  const saveNewDoseOption = async (newOption) => {
    try {
      // Get the userId from localStorage
      const userId = localStorage.getItem("userId") || "1";
      
      const response = await fetch(`${C.getUrl()}/patientOpVisit/saveDoseMaster`, {
        method: 'POST', // Assuming POST for create based on your endpoint naming
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          doseName: newOption,
          doseDescription: `${newOption} description`, // Optional: create a default description
          activeInd: "Y",
          createdByDTO: {
            userId: parseInt(userId)
          },
          updatedByDTO: {
            userId: parseInt(userId)
          }
        }),
      });
      
      if (!response.ok) {
        console.warn(`Failed to save new dose option. Status: ${response.status}`);
        return false;
      }
      
      const data = await response.json();
      
      if (data && (data.statusCode === 200 || data.status === "SUCCESS")) {
        // Refresh the options after saving a new one
        fetchDoseOptions();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error saving new dose option:', error);
      return false;
    }
  };

  const fetchWhenOptions = async () => {
    try {
      console.log("Fetching when options...");
      const response = await fetch(`${C.getUrl()}/patientOpVisit/getAllWhenMaster`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      // Log the raw response for debugging
      const responseText = await response.text();
      console.log("When API response:", responseText);
      
      if (!response.ok) {
        console.error(`Failed to fetch when options. Status: ${response.status}`);
        return;
      }
      
      // Try to parse the response as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Error parsing response as JSON:", parseError);
        return;
      }
      
      if (data && data.statusCode === 200) {
        try {
          const messageStr = data.message || '[]';
          
          // The message appears to be incomplete in your example, so handle it carefully
          let whenArray = [];
          try {
            // If the message is incomplete JSON, try to fix it or use an empty array
            whenArray = JSON.parse(messageStr.endsWith('}]') ? messageStr : messageStr + '}]');
          } catch (e) {
            console.error('Error parsing when data:', e);
            whenArray = [];
          }
          
          // Extract when values - assuming 'whenName' is the field we want
          const options = whenArray.map(item => item.whenName || '').filter(Boolean);
          console.log("Extracted when options:", options);
          
          if (options.length > 0) {
            setFetchedWhenOptions(options);
          }
        } catch (messageParseError) {
          console.error("Error parsing message field:", messageParseError);
        }
      }
    } catch (error) {
      console.error('Error fetching when options:', error);
    }
  };
  
  const saveNewWhenOption = async (newOption) => {
    try {
      // Get the userId from localStorage
      const userId = localStorage.getItem("userId") || "1";
      
      const response = await fetch(`${C.getUrl()}/patientOpVisit/saveWhenMaster`, {
        method: 'POST', // Assuming POST for create based on your endpoint naming
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          whenName: newOption,
          whenDescription: `${newOption} description`, // Optional: create a default description
          activeInd: "Y",
          createdByDTO: {
            userId: parseInt(userId)
          },
          updatedByDTO: {
            userId: parseInt(userId)
          }
        }),
      });
      
      if (!response.ok) {
        console.warn(`Failed to save new when option. Status: ${response.status}`);
        return false;
      }
      
      const data = await response.json();
      
      if (data && (data.statusCode === 200 || data.status === "SUCCESS")) {
        // Refresh the options after saving a new one
        fetchWhenOptions();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error saving new when option:', error);
      return false;
    }
  };

  const fetchComorbidityOptions = async () => {
    try {
      console.log("Fetching comorbidity options...");
      const response = await fetch(`${C.getUrl()}/medicalComorbidityMaster/getAllComorbidities`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      // Log the raw response for debugging
      const responseText = await response.text();
      console.log("Comorbidity API response:", responseText);
      
      if (!response.ok) {
        console.error(`Failed to fetch comorbidity options. Status: ${response.status}`);
        return;
      }
      
      // Try to parse the response as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Error parsing response as JSON:", parseError);
        return;
      }
      
      if (data && data.statusCode === 200) {
        try {
          const messageStr = data.message || '[]';
          const comorbiditiesArray = JSON.parse(messageStr);
          
          // Note: the API field is "comborty" rather than "comorbidity"
          const options = comorbiditiesArray.map(item => item.comborty || '').filter(Boolean);
          console.log("Extracted comorbidity options:", options);
          
          if (options.length > 0) {
            setFetchedComorbidityOptions(options);
          }
        } catch (messageParseError) {
          console.error("Error parsing message field:", messageParseError);
        }
      }
    } catch (error) {
      console.error('Error fetching comorbidity options:', error);
    }
  };
  
  const saveNewComorbidityOption = async (newOption) => {
    try {
      const response = await fetch(`${C.getUrl()}/medicalComorbidityMaster/create`, {
        method: 'PUT', // Using PUT as specified
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          comborty: newOption, // Note: the API field is "comborty" rather than "comorbidity"
          activeInd: "Y"
        }),
      });
      
      if (!response.ok) {
        console.warn(`Failed to save new comorbidity option. Status: ${response.status}`);
        return false;
      }
      
      const data = await response.json();
      
      if (data && data.statusCode === 200) {
        // Refresh the options after saving a new one
        fetchComorbidityOptions();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error saving new comorbidity option:', error);
      return false;
    }
  };
  

 // Enhanced useEffect hook to ensure proper data refresh
useEffect(() => {
  if (props.patientId && props.patientId !== '') {
    setPatientId(props.patientId);
    fetchPatientData(props.patientId);
    fetchAllMedicalHistory(props.patientId);
    fetchPatientSummaryDetails(props.patientId);
    fetchSpecificationOptions();
    fetchRelationOptions(); 
    fetchAllergyOptions(); 
    fetchAddictionTypeOptions();
    fetchAllergyTypeOptions();
    fetchDurationOptions();
    fetchMedicineOptions();
    fetchDoseOptions();
    fetchWhenOptions();
    fetchComorbidityOptions();
    
    // Reset deleted rows tracking on initial load
    // This ensures we're not accidentally tracking stale IDs
    setDeletedRowsTracking({
      deletedAddictionTable: [],
      deletedAllergiesTable: [],
      deletedFamilyTable: [],
      deletedMedicationTable: [],
      deletedComorbidityTable: []
    });
  }
}, [props.patientId]);

// Add this useEffect to persist deletedRowsTracking in localStorage
useEffect(() => {
  // Save the deleted rows tracking to localStorage when it changes
  if (Object.values(deletedRowsTracking).some(arr => arr.length > 0)) {
    localStorage.setItem(`deletedRows_${patientId}`, JSON.stringify(deletedRowsTracking));
    console.log('Saved deleted rows tracking to localStorage:', deletedRowsTracking);
  }
}, [deletedRowsTracking, patientId]);

// Add this code at the beginning of your component to load deletedRowsTracking from localStorage
useEffect(() => {
  if (patientId) {
    const savedTracking = localStorage.getItem(`deletedRows_${patientId}`);
    if (savedTracking) {
      try {
        const parsedTracking = JSON.parse(savedTracking);
        setDeletedRowsTracking(parsedTracking);
        console.log('Loaded deleted rows tracking from localStorage:', parsedTracking);
      } catch (error) {
        console.error('Error parsing saved tracking data:', error);
      }
    }
  }
}, [patientId]);

// Helper function to filter out inactive records - improved version
const filterActiveRecords = (records, deletedIdsArray = []) => {
  if (!records || !records.length) return [];
  
  console.log(`Before filtering: ${records.length} records`);
  
  // Ensure deletedIdsArray is an array
  const deletedIds = Array.isArray(deletedIdsArray) ? deletedIdsArray : [];
  
  // Create a more robust filter
  const filteredRecords = records.filter(item => {
    // Skip null or undefined items
    if (!item) return false;
    
    // Try to get the ID using various possible field names
    const recordId = item.id || 
      item.addictionHistoryId || 
      item.allergyHistoryId || 
      item.familyHistoryId || 
      item.currentMedicationId || 
      item.comorbidityId ||
      item.patientAddictionHistoryId || 
      item.patientAllergyHistoryId || 
      item.patientFamilyHistoryId || 
      item.patientCurrentMedicationId || 
      item.patientComorbidityId || 
      null;
    
    // CRITICAL: Only include records that are explicitly marked as active
    // Check various formats of activeInd: 'Y', true, 'true', 1, etc.
    const activeIndRaw = item.activeInd;
    const isActive = activeIndRaw === 'Y' || 
                    activeIndRaw === true || 
                    activeIndRaw === 'true' || 
                    activeIndRaw === 1 || 
                    activeIndRaw === '1';
    
    // Check if this ID is in our deleted array
    const isNotDeleted = !recordId || !deletedIds.includes(recordId);
    
    // Log details for debugging
    console.log(`Filtering record ID ${recordId || 'unknown'}: activeInd=${activeIndRaw}, isActive=${isActive}, isNotDeleted=${isNotDeleted}`);
    
    // Only include records that are both active AND not in our deleted tracking array
    return isActive && isNotDeleted;
  });
  
  console.log(`After filtering: ${filteredRecords.length} records remain`);
  return filteredRecords;
};

  // Data fetching functions with async/await and try/catch
  const fetchPatientData = async (patientId) => {
    if (!patientId) return;
  
    try {
      // Use a proper patient data endpoint instead
      const response = await fetch(`${C.getUrl()}/patient/${patientId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        // If this endpoint doesn't exist either, we can fall back to a basic placeholder
        console.warn(`Patient info endpoint not found. Status: ${response.status}`);
        // Set some default values since we can't fetch real data
        setPatientData({
          firstName: '',
          lastName: '',
          gender: '',
          age: '',
          contact: '',
          patientId: patientId,
          status: '',
          consultant: '',
          currentAddress: '',
          referral: '',
          allergies: '',
          comorbidities: '',
          biomarker: '',
          dob: ''
        });
        return;
      }
      
      const responseData = await response.json();
      
      if (responseData && responseData.success) {
        const data = responseData.data;
        setPatientData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          gender: data.gender || '',
          age: data.age || '',
          contact: data.phoneNumber1 || '',
          patientId: data.caseNo || '',
          status: data.status || 'New Patient',
          consultant: data.consultant || '',
          currentAddress: data.address || '',
          referral: data.referralSource || '',
          allergies: data.allergies || '',
          comorbidities: data.comorbidities || '',
          biomarker: data.biomarker || '',
          dob: data.dob || ''
        });
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
      // Set default values if we can't fetch patient data
      setPatientData({
        firstName: '',
        lastName: '',
        gender: '',
        age: '',
        contact: '',
        patientId: patientId,
        status: 'New Patient',
        consultant: '',
        currentAddress: '',
        referral: '',
        allergies: '',
        comorbidities: '',
        biomarker: '',
        dob: ''
      });
    }
  };

// Modified fetchAllMedicalHistory function with explicit filtering for inactive records
const fetchAllMedicalHistory = async (patientId) => {
  if (!patientId) return;

  // Create an object to store all fetched data
  const allData = {
    addictionHistories: [],
    allergies: [],
    familyHistories: [],
    pastMedicalHistories: [],
    pastOncologyHistories: [],
    pastSurgicalHistories: [],
    comorbidities: [],
    currentMedications: [],
    reproductiveHistory: null
  };

  try {
    // Load deleted rows tracking from localStorage if available
    let localDeletedTracking = null;
    try {
      const savedTracking = localStorage.getItem(`deletedRows_${patientId}`);
      if (savedTracking) {
        localDeletedTracking = JSON.parse(savedTracking);
        console.log('Local deleted tracking loaded:', localDeletedTracking);
      }
    } catch (e) {
      console.error('Error loading deleted tracking:', e);
    }

    // Fetch addiction history
    const addictionResponse = await fetch(`${C.getUrl()}/medicalHistory/addictionHistory/patient/${patientId}/addictionHistory`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    
    if (addictionResponse.ok) {
      const addictionData = await addictionResponse.json();
      if (addictionData && addictionData.statusCode === 200) {
        let parsedData = JSON.parse(addictionData.message || '[]');
        
        // Pre-filter inactive records from addiction history
        parsedData = parsedData.filter(item => {
          const isActive = item.activeInd === 'Y' || item.activeInd === true || item.activeInd === 'true' || item.activeInd === 1;
          
          // Check if this ID is in our local deleted tracking
          const recordId = item.id || item.addictionHistoryId || item.patientAddictionHistoryId;
          const isTrackedAsDeleted = localDeletedTracking && 
                                  localDeletedTracking.deletedAddictionTable && 
                                  localDeletedTracking.deletedAddictionTable.includes(recordId);
          
          return isActive && !isTrackedAsDeleted;
        });
        
        console.log("Fetched and filtered addiction histories:", parsedData);
        allData.addictionHistories = parsedData;
      }
    }

    // Fetch allergy history with filtering
    const allergyResponse = await fetch(`${C.getUrl()}/medicalHistory/allergyHistory/patient/${patientId}/allergyHistory`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    
    if (allergyResponse.ok) {
      const allergyData = await allergyResponse.json();
      if (allergyData && allergyData.statusCode === 200) {
        let parsedData = JSON.parse(allergyData.message || '[]');
        
        // Pre-filter inactive allergy records
        parsedData = parsedData.filter(item => {
          const isActive = item.activeInd === 'Y' || item.activeInd === true || item.activeInd === 'true' || item.activeInd === 1;
          
          // Check if this ID is in our local deleted tracking
          const recordId = item.id || item.allergyHistoryId || item.patientAllergyHistoryId;
          const isTrackedAsDeleted = localDeletedTracking && 
                                  localDeletedTracking.deletedAllergiesTable && 
                                  localDeletedTracking.deletedAllergiesTable.includes(recordId);
          
          return isActive && !isTrackedAsDeleted;
        });
        
        allData.allergies = parsedData;
      }
    }

    // Fetch family history with filtering
    const familyResponse = await fetch(`${C.getUrl()}/medicalHistory/familyHistory/patient/${patientId}/familyHistory`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    
    if (familyResponse.ok) {
      const familyData = await familyResponse.json();
      if (familyData && familyData.statusCode === 200) {
        let parsedData = JSON.parse(familyData.message || '[]');
        
        // Pre-filter inactive family history records
        parsedData = parsedData.filter(item => {
          const isActive = item.activeInd === 'Y' || item.activeInd === true || item.activeInd === 'true' || item.activeInd === 1;
          
          // Check if this ID is in our local deleted tracking
          const recordId = item.id || item.familyHistoryId || item.patientFamilyHistoryId;
          const isTrackedAsDeleted = localDeletedTracking && 
                                  localDeletedTracking.deletedFamilyTable && 
                                  localDeletedTracking.deletedFamilyTable.includes(recordId);
          
          return isActive && !isTrackedAsDeleted;
        });
        
        allData.familyHistories = parsedData;
      }
    }

    // Fetch past medical history
    const medicalResponse = await fetch(`${C.getUrl()}/medicalHistory/pastMedicalHistory/patient/${patientId}/pastMedicalHistory`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    
    if (medicalResponse.ok) {
      const medicalData = await medicalResponse.json();
      if (medicalData && medicalData.statusCode === 200) {
        let parsedData = JSON.parse(medicalData.message || '[]');
        
        // Filter inactive medical history records
        parsedData = parsedData.filter(item => 
          item.activeInd === 'Y' || item.activeInd === true || item.activeInd === 'true' || item.activeInd === 1
        );
        
        allData.pastMedicalHistories = parsedData;
      }
    }

    // Fetch past oncology history with filtering
try {
  const oncologyResponse = await fetch(`${C.getUrl()}/medicalHistory/pastOncologyHistory/patient/${patientId}/pastOncologyHistory`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  
  if (oncologyResponse.ok) {
    try {
      const oncologyData = await oncologyResponse.json();
      
      if (oncologyData && oncologyData.statusCode === 200) {
        try {
          let parsedData = JSON.parse(oncologyData.message || '[]');
          
          // Filter inactive oncology history records
          parsedData = parsedData.filter(item => 
            item.activeInd === 'Y' || item.activeInd === true || item.activeInd === 'true' || item.activeInd === 1
          );
          
          console.log("Parsed oncology histories (original):", parsedData);
          
          // Check if we have a stored value from previous interactions
          const storedStatus = window.localStorage.getItem('pastOncologyHistoryStatus');
          
          if (parsedData.length > 0) {
            // Check for "Don't Know" status specifically and preserve it
            const status = parsedData[0].oncologyHistoryStatus || 
                          parsedData[0].pastOncologyHistoryStatus || 
                          parsedData[0].status || "";
                          
            console.log("Raw status from database:", status);
            
            // If the status is "DONT_KNOW" or similar, make sure we preserve it exactly
            if (typeof status === 'string' && 
                (status.toUpperCase() === 'DONT_KNOW' || 
                 status.toUpperCase() === "DON'T KNOW" || 
                 status.toUpperCase() === 'D')) {
              console.log("Found 'Don't Know' status in database, preserving it");
              parsedData[0].oncologyHistoryStatus = "DONT_KNOW";
              parsedData[0].pastOncologyHistoryStatus = "DONT_KNOW";
              parsedData[0].status = "DONT_KNOW";
              window.localStorage.setItem('pastOncologyHistoryStatus', "Don't Know");
            }
            
            // Preserve the exact status from the database
            allData.pastOncologyHistories = parsedData;
            console.log("Using fetched oncology history data with status:", 
              parsedData[0].oncologyHistoryStatus || 
              parsedData[0].pastOncologyHistoryStatus || 
              parsedData[0].status);
          } else if (storedStatus === "Don't Know") {
            // If we have a stored "Don't Know" status, use it
            console.log("No oncology data found, but using stored 'Don't Know' status");
            allData.pastOncologyHistories = [{
              oncologyHistoryStatus: "DONT_KNOW",
              pastOncologyHistoryStatus: "DONT_KNOW",
              status: "DONT_KNOW",
              history: ""
            }];
          } else {
            // Only set default if no data exists and no stored "Don't Know" status
            allData.pastOncologyHistories = [{
              oncologyHistoryStatus: "NO_ONCOLOGY_HISTORY",
              pastOncologyHistoryStatus: "NO_ONCOLOGY_HISTORY",
              status: "NO_ONCOLOGY_HISTORY",
              history: ""
            }];
          }
        } catch (parseError) {
          console.error("Error parsing oncology history:", parseError);
          
          // Check if we have a stored value from previous interactions
          const storedStatus = window.localStorage.getItem('pastOncologyHistoryStatus');
          
          if (storedStatus === "Don't Know") {
            // If we have a stored "Don't Know" status, use it
            allData.pastOncologyHistories = [{
              oncologyHistoryStatus: "DONT_KNOW",
              pastOncologyHistoryStatus: "DONT_KNOW",
              status: "DONT_KNOW",
              history: ""
            }];
          } else {
            allData.pastOncologyHistories = [{
              oncologyHistoryStatus: "NO_ONCOLOGY_HISTORY",
              pastOncologyHistoryStatus: "NO_ONCOLOGY_HISTORY",
              status: "NO_ONCOLOGY_HISTORY",
              history: ""
            }];
          }
        }
      }
    } catch (jsonError) {
      console.error("Error parsing oncology response as JSON:", jsonError);
      
      // Check if we have a stored value from previous interactions
      const storedStatus = window.localStorage.getItem('pastOncologyHistoryStatus');
      
      if (storedStatus === "Don't Know") {
        // If we have a stored "Don't Know" status, use it
        allData.pastOncologyHistories = [{
          oncologyHistoryStatus: "DONT_KNOW",
          pastOncologyHistoryStatus: "DONT_KNOW",
          status: "DONT_KNOW",
          history: ""
        }];
      } else {
        allData.pastOncologyHistories = [{
          oncologyHistoryStatus: "NO_ONCOLOGY_HISTORY",
          pastOncologyHistoryStatus: "NO_ONCOLOGY_HISTORY",
          status: "NO_ONCOLOGY_HISTORY",
          history: ""
        }];
      }
    }
  }
} catch (fetchError) {
  console.error("Error fetching oncology history:", fetchError);
  
  // Check if we have a stored value from previous interactions
  const storedStatus = window.localStorage.getItem('pastOncologyHistoryStatus');
  
  if (storedStatus === "Don't Know") {
    // If we have a stored "Don't Know" status, use it
    allData.pastOncologyHistories = [{
      oncologyHistoryStatus: "DONT_KNOW",
      pastOncologyHistoryStatus: "DONT_KNOW",
      status: "DONT_KNOW",
      history: ""
    }];
  } else {
    allData.pastOncologyHistories = [{
      oncologyHistoryStatus: "NO_ONCOLOGY_HISTORY",
      pastOncologyHistoryStatus: "NO_ONCOLOGY_HISTORY",
      status: "NO_ONCOLOGY_HISTORY",
      history: ""
    }];
  }
}

    // Fetch past surgical history with filtering
    const surgicalResponse = await fetch(`${C.getUrl()}/medicalHistory/pastSurgicalHistory/patient/${patientId}/pastSurgicalHistory`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    
    if (surgicalResponse.ok) {
      const surgicalData = await surgicalResponse.json();
      if (surgicalData && surgicalData.statusCode === 200) {
        try {
          let parsedData = JSON.parse(surgicalData.message || '[]');
          
          // Filter inactive surgical history records
          parsedData = parsedData.filter(item => 
            item.activeInd === 'Y' || item.activeInd === true || item.activeInd === 'true' || item.activeInd === 1
          );
          
          allData.pastSurgicalHistories = parsedData;
        } catch (parseError) {
          console.error("Error parsing surgical history:", parseError);
          allData.pastSurgicalHistories = [];
        }
      }
    }

    // Fetch comorbidities with filtering
    const comorbidityResponse = await fetch(`${C.getUrl()}/medicalHistory/comorbidities/patient/${patientId}/comorbidities`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    
    if (comorbidityResponse.ok) {
      const comorbidityData = await comorbidityResponse.json();
      if (comorbidityData && comorbidityData.statusCode === 200) {
        let parsedData = JSON.parse(comorbidityData.message || '[]');
        
        // Pre-filter inactive comorbidity records
        parsedData = parsedData.filter(item => {
          const isActive = item.activeInd === 'Y' || item.activeInd === true || item.activeInd === 'true' || item.activeInd === 1;
          
          // Check if this ID is in our local deleted tracking
          const recordId = item.id || item.comorbidityId || item.patientComorbidityId;
          const isTrackedAsDeleted = localDeletedTracking && 
                                  localDeletedTracking.deletedComorbidityTable && 
                                  localDeletedTracking.deletedComorbidityTable.includes(recordId);
          
          return isActive && !isTrackedAsDeleted;
        });
        
        allData.comorbidities = parsedData;
      }
    }

    // Fetch current medications with filtering
    const medicationResponse = await fetch(`${C.getUrl()}/medicalHistory/currentMedication/patient/${patientId}/currentMedication`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    
    if (medicationResponse.ok) {
      const medicationData = await medicationResponse.json();
      if (medicationData && medicationData.statusCode === 200) {
        let parsedData = JSON.parse(medicationData.message || '[]');
        
        // Pre-filter inactive medication records
        parsedData = parsedData.filter(item => {
          const isActive = item.activeInd === 'Y' || item.activeInd === true || item.activeInd === 'true' || item.activeInd === 1;
          
          // Check if this ID is in our local deleted tracking
          const recordId = item.id || item.currentMedicationId || item.patientCurrentMedicationId;
          const isTrackedAsDeleted = localDeletedTracking && 
                                  localDeletedTracking.deletedMedicationTable && 
                                  localDeletedTracking.deletedMedicationTable.includes(recordId);
          
          return isActive && !isTrackedAsDeleted;
        });
        
        allData.currentMedications = parsedData;
      }
    }

    // Fetch reproductive history
    const reproductiveResponse = await fetch(`${C.getUrl()}/medicalHistory/reproductiveHistory/patient/${patientId}/reproductiveHistory`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    
    if (reproductiveResponse.ok) {
      const reproductiveData = await reproductiveResponse.json();
      if (reproductiveData && reproductiveData.statusCode === 200) {
        let parsedData = JSON.parse(reproductiveData.message || '[]');
        
        // Filter inactive reproductive history records
        parsedData = parsedData.filter(item => 
          item.activeInd === 'Y' || item.activeInd === true || item.activeInd === 'true' || item.activeInd === 1
        );
        
        if (parsedData.length > 0) {
          allData.reproductiveHistory = parsedData[0];
        }
      }
    }

    // Process all the data after fetching everything
    console.log("All filtered data before processing:", allData);
    processHistoryData(allData);
    
  } catch (error) {
    console.error('Error fetching medical history:', error);
    swal({
      title: "Error",
      text: "Failed to load medical history data",
      icon: "error",
      button: "OK",
    });
  }
};

// Helper function to convert API enum values to user-friendly display values
  const getDisplayValue = (apiValue) => {
    // Map API enum values to user-friendly display values
    const displayMap = {
      'NO_HISTORY_OF_ADDICTION': 'No History of Addiction',
      'NO_HISTORY_OF_ALLERGY': 'No History of Allergies',
      'NO_HISTORY_OF_FAMILY_CONDITION': 'No History of Family',
      'NO_CURRENT_MEDICATION': 'No Current Medication',
      'NO_MEDICAL_HISTORY': 'No Past Medical History',
      'NO_COMORBIDITIES': 'No History of Comorbidities',
      'NO_SURGICAL_HISTORY': 'No Past Surgical History',
      'NO_ONCOLOGY_HISTORY': 'No Past Oncology History',
      'DONT_KNOW': 'Don\'t Know'
    };
    
    // Check for case-insensitive match for DONT_KNOW with improved handling
    if (typeof apiValue === 'string') {
      const upperValue = apiValue.toUpperCase();
      if (upperValue === 'DONT_KNOW' || upperValue === "DON'T KNOW" || upperValue === 'D') {
        return 'Don\'t Know';
      }
      
      // Also check for oncology history specifically
      if (upperValue === 'NO_ONCOLOGY_HISTORY' || upperValue === 'NO PAST ONCOLOGY HISTORY') {
        return 'No Past Oncology History';
      }
      
      // Check for surgical history specifically
      if (upperValue === 'NO_SURGICAL_HISTORY' || upperValue === 'NO PAST SURGICAL HISTORY' || upperValue === 'NO SURGICAL HISTORY') {
        return 'No Past Surgical History';
      }
    }
    
    return displayMap[apiValue] || apiValue;
  };

const processHistoryData = (data) => {
  // Note: Most filtering should now happen in fetchAllMedicalHistory before this function is called
  // This is an additional safety check
  const ensureActiveRecords = (records, deletedIdsArray = []) => {
    if (!records || !records.length) return [];
    
    // Filter one more time to ensure only active records are processed
    return records.filter(item => {
      if (!item) return false;
      
      // Get record ID from various possible fields
      const recordId = item.id || 
        item.addictionHistoryId || 
        item.allergyHistoryId || 
        item.familyHistoryId || 
        item.currentMedicationId || 
        item.comorbidityId ||
        item.patientAddictionHistoryId || 
        item.patientAllergyHistoryId || 
        item.patientFamilyHistoryId || 
        item.patientCurrentMedicationId || 
        item.patientComorbidityId || 
        null;
      
      // Check active status (various formats)
      const isActive = item.activeInd === 'Y' || 
                       item.activeInd === true || 
                       item.activeInd === 'true' || 
                       item.activeInd === 1 || 
                       item.activeInd === '1';
      
      // Check if record is in deleted tracking array
      const isTrackedAsDeleted = recordId && deletedIdsArray.includes(recordId);
      
      return isActive && !isTrackedAsDeleted;
    });
  };

  // Set addiction history
  if (data.addictionHistories && data.addictionHistories.length > 0) {
    console.log('Processing addiction histories:', data.addictionHistories);
    
    const apiStatus = data.addictionHistories[0].addictionStatus || "";
    const status = getDisplayValue(apiStatus);
    setAddictionHistory(status);
    
    // If status is 'Yes', populate the addiction table with fetched data
    if (status === 'Yes' && data.addictionHistories.length > 0) {
      // Do one final filter to ensure only active records are shown
      const filteredData = ensureActiveRecords(data.addictionHistories, deletedRowsTracking.deletedAddictionTable || []);
      console.log('Final filtered addiction histories:', filteredData);
      
      if (filteredData.length > 0) {
        const tableData = filteredData.map(item => ({
          id: item.id || item.addictionHistoryId || item.patientAddictionHistoryId, 
          historyOfAddiction: item.addictionMasterDTO?.addiction || item.addictionHistory || '',
          startedAtAge: item.age ? item.age.toString() : '',
          perDay: item.perDay || '',
          duration: item.duration || '',
          reformade: item.reformade || '',
          comments: item.comment || ''
        }));
        
        // Add an empty row at the end if needed
        if (tableData.every(row => row.historyOfAddiction && row.historyOfAddiction.trim() !== '')) {
          tableData.push({ id: null, historyOfAddiction: '', startedAtAge: '', perDay: '', duration: '', reformade: '', comments: '' });
        }
        
        setAddictionTable(tableData);
      } else {
        // No active records, set a single empty row
        setAddictionTable([{ id: null, historyOfAddiction: '', startedAtAge: '', perDay: '', duration: '', reformade: '', comments: '' }]);
      }
    } else {
      // Reset to empty table with one empty row for non-Yes statuses
      setAddictionTable([{ id: null, historyOfAddiction: '', startedAtAge: '', perDay: '', duration: '', reformade: '', comments: '' }]);
    }
  }

  // Set allergies history
  if (data.allergies && data.allergies.length > 0) {
    const allergyData = data.allergies.find(a => a.allergyStatus);
    const apiStatus = allergyData ? allergyData.allergyStatus : "";
    const status = getDisplayValue(apiStatus);
    setAllergiesHistory(status);
    
    // If status is "Yes", populate the allergies table with fetched data
    if (status === 'Yes' && data.allergies.length > 0) {
      // Do one final filter to ensure only active records are shown
      const filteredData = ensureActiveRecords(data.allergies, deletedRowsTracking.deletedAllergiesTable || []);
      console.log('Final filtered allergies data:', filteredData);
      
      if (filteredData.length > 0) {
        const tableData = filteredData.map(item => ({
          id: item.id || item.allergyHistoryId || item.patientAllergyHistoryId,
          allergy: item.allergyHistory || '',
          allergyType: item.allergyType || item.allergy_Type || '',
          duration: item.duration || '',
          comment: item.comment || ''
        }));
        
        // Add an empty row at the end if needed
        if (tableData.every(row => row.allergy && row.allergy.trim() !== '')) {
          tableData.push({ id: null, allergy: '', allergyType: '', duration: '', comment: '' });
        }
        
        setAllergiesTable(tableData);
      } else {
        // No active records, set a single empty row
        setAllergiesTable([{ id: null, allergy: '', allergyType: '', duration: '', comment: '' }]);
      }
    } else {
      // Reset to empty table with one empty row for non-Yes statuses
      setAllergiesTable([{ id: null, allergy: '', allergyType: '', duration: '', comment: '' }]);
    }
  }

  // Set family history
  if (data.familyHistories && data.familyHistories.length > 0) {
    const familyData = data.familyHistories.find(f => f.familyHistoryStatus);
    const apiStatus = familyData ? familyData.familyHistoryStatus : "";
    const status = getDisplayValue(apiStatus);
    setFamilyHistory(status);
    
    // If status is "Yes", populate the family history table with fetched data
    if (status === 'Yes' && data.familyHistories.length > 0) {
      // Do one final filter to ensure only active records are shown
      const filteredData = ensureActiveRecords(data.familyHistories, deletedRowsTracking.deletedFamilyTable || []);
      console.log('Final filtered family history data:', filteredData);
      
      if (filteredData.length > 0) {
        const tableData = filteredData.map(item => ({
          id: item.id || item.familyHistoryId || item.patientFamilyHistoryId,
          historyOf: item.history || '',
          typeOfRelation: item.relation || '',
          atTheAgeOf: item.age ? item.age.toString() : '',
          specification: item.specification || '',
          comment: item.comment || ''
        }));
        
        // Add an empty row at the end if needed
        if (tableData.every(row => row.historyOf && row.historyOf.trim() !== '')) {
          tableData.push({ id: null, historyOf: '', typeOfRelation: '', atTheAgeOf: '', specification: '', comment: '' });
        }
        
        setFamilyTable(tableData);
      } else {
        // No active records, set a single empty row
        setFamilyTable([{ id: null, historyOf: '', typeOfRelation: '', atTheAgeOf: '', specification: '', comment: '' }]);
      }
    } else {
      // Reset to empty table with one empty row for non-Yes statuses
      setFamilyTable([{ id: null, historyOf: '', typeOfRelation: '', atTheAgeOf: '', specification: '', comment: '' }]);
    }
  }

  // Set current medication
  if (data.currentMedications && data.currentMedications.length > 0) {
    const apiStatus = data.currentMedications[0].medicationStatus || "";
    const status = getDisplayValue(apiStatus);
    setCurrentMedication(status);
    
    // If status is "Yes", populate the medication table with fetched data
    if (status === 'Yes' && data.currentMedications.length > 0) {
      // Do one final filter to ensure only active records are shown
      const filteredData = ensureActiveRecords(data.currentMedications, deletedRowsTracking.deletedMedicationTable || []);
      console.log('Final filtered medication data:', filteredData);
      
      if (filteredData.length > 0) {
        const tableData = filteredData.map(item => ({
          id: item.id || item.currentMedicationId || item.patientCurrentMedicationId,
          medicine: item.medicine || '',
          dose: item.dose || '',
          when: item.when || '',
          duration: item.duration || '',
          instructions: item.instruction || ''
        }));
        
        // Add an empty row at the end if needed
        if (tableData.every(row => row.medicine && row.medicine.trim() !== '')) {
          tableData.push({ id: null, medicine: '', dose: '', when: '', duration: '', instructions: '' });
        }
        
        setMedicationTable(tableData);
      } else {
        // No active records, set a single empty row
        setMedicationTable([{ id: null, medicine: '', dose: '', when: '', duration: '', instructions: '' }]);
      }
    } else {
      // Reset to empty table with one empty row for non-Yes statuses
      setMedicationTable([{ id: null, medicine: '', dose: '', when: '', duration: '', instructions: '' }]);
    }
  }

  // Set past medical history
  if (data.pastMedicalHistories && data.pastMedicalHistories.length > 0) {
    const medicalData = data.pastMedicalHistories[0];
    const apiStatus = medicalData.medicalHistoryStatus || "";
    const status = getDisplayValue(apiStatus);
    setPastMedicalHistory(status);
    setPastMedicalHistoryDetails(medicalData.history || "");
  } else {
    setPastMedicalHistory("");
    setPastMedicalHistoryDetails("");
  }

  // Set comorbidities
  if (data.comorbidities && data.comorbidities.length > 0) {
    const apiStatus = data.comorbidities[0].comorbidityStatus || "No History of Comorbidities";
    const status = getDisplayValue(apiStatus);
    setComorbidities(status);
    
    // If status is "Yes", populate the comorbidity table with fetched data
    if (status === 'Yes' && data.comorbidities.length > 0) {
      // Do one final filter to ensure only active records are shown
      const filteredData = ensureActiveRecords(data.comorbidities, deletedRowsTracking.deletedComorbidityTable || []);
      console.log('Final filtered comorbidity data:', filteredData);
      
      if (filteredData.length > 0) {
        const tableData = filteredData.map(item => ({
          id: item.id || item.comorbidityId || item.patientComorbidityId,
          comorbidity: item.comorbidity || '',
          duration: item.duration || '',
          onMedication: item.medication === 'Yes' ? 'Yes' : 'No',
          comment: item.comment || ''
        }));
        
        // Add an empty row at the end if needed
        if (tableData.every(row => row.comorbidity && row.comorbidity.trim() !== '')) {
          tableData.push({ id: null, comorbidity: '', duration: '', onMedication: '', comment: '' });
        }
        
        setComorbidityTable(tableData);
      } else {
        // No active records, set a single empty row
        setComorbidityTable([{ id: null, comorbidity: '', duration: '', onMedication: '', comment: '' }]);
      }
    } else {
      // Reset to empty table with one empty row for non-Yes statuses
      setComorbidityTable([{ id: null, comorbidity: '', duration: '', onMedication: '', comment: '' }]);
    }
  }

  // Set past surgical history
  if (data.pastSurgicalHistories && data.pastSurgicalHistories.length > 0) {
    console.log("Processing past surgical history:", data.pastSurgicalHistories);
    const surgicalData = data.pastSurgicalHistories[0];
    
    // First, check if history contains a status value like "Don't Know"
    let status = "";
    
    // Check if history field contains a status indicator
    if (surgicalData.history) {
      const historyValue = surgicalData.history.trim();
      if (historyValue.toUpperCase() === "DON'T KNOW" || 
          historyValue.toUpperCase() === "DONT_KNOW" || 
          historyValue.toUpperCase() === "DONT KNOW") {
        status = "Don't Know";
      } else if (historyValue.toUpperCase() === "NO PAST SURGICAL HISTORY" || 
                historyValue.toUpperCase() === "NO_SURGICAL_HISTORY" || 
                historyValue.toUpperCase() === "NO SURGICAL HISTORY") {
        status = "No Past Surgical History";
      }
    }
    
    // If no status found in history, check dedicated status fields
    if (!status) {
      // Check for various forms of NO_SURGICAL_HISTORY in status fields
      const rawStatus = surgicalData.surgicalHistoryStatus || 
                       surgicalData.pastSurgicalHistoryStatus || 
                       surgicalData.status || "";
      
      if (typeof rawStatus === 'string') {
        const upperStatus = rawStatus.toUpperCase();
        if (upperStatus === "NO_SURGICAL_HISTORY" || 
            upperStatus === "NO SURGICAL HISTORY" || 
            upperStatus === "NO PAST SURGICAL HISTORY") {
          status = "No Past Surgical History";
        } else if (upperStatus === "DONT_KNOW" || 
                  upperStatus === "DON'T KNOW" || 
                  upperStatus === "DONT KNOW") {
          status = "Don't Know";
        } else if (upperStatus === "YES" || upperStatus === "Y") {
          status = "Yes";
        } else if (rawStatus) {
          // Any other non-empty status
          status = rawStatus;
        }
      }
      
      // Only default to Yes if history has content and isn't a status indicator
      if (!status && surgicalData.history && 
          surgicalData.history.trim() !== "" && 
          surgicalData.history.trim().toUpperCase() !== "DON'T KNOW" && 
          surgicalData.history.trim().toUpperCase() !== "DONT_KNOW" && 
          surgicalData.history.trim().toUpperCase() !== "DONT KNOW" && 
          surgicalData.history.trim().toUpperCase() !== "NO PAST SURGICAL HISTORY" && 
          surgicalData.history.trim().toUpperCase() !== "NO_SURGICAL_HISTORY" && 
          surgicalData.history.trim().toUpperCase() !== "NO SURGICAL HISTORY") {
        status = 'Yes';
      }
    }
    
    console.log("Determined surgical history status:", status);
    setPastSurgicalHistory(status);
    
    // Then set the details based on the status
    if (status === 'Yes') {
      // Only use history as details if it's not a status indicator
      const historyValue = surgicalData.history ? surgicalData.history.trim() : "";
      const isStatusIndicator = historyValue.toUpperCase() === "DON'T KNOW" || 
                               historyValue.toUpperCase() === "DONT_KNOW" || 
                               historyValue.toUpperCase() === "DONT KNOW" || 
                               historyValue.toUpperCase() === "NO PAST SURGICAL HISTORY" || 
                               historyValue.toUpperCase() === "NO_SURGICAL_HISTORY" || 
                               historyValue.toUpperCase() === "NO SURGICAL HISTORY";
      
      setPastSurgicalHistoryDetails(isStatusIndicator ? surgicalData.details || "" : surgicalData.history || surgicalData.details || "");
    } else {
      setPastSurgicalHistoryDetails("");
    }
  } else {
    // If no data found, set empty values instead of defaults
    setPastSurgicalHistory("");
    setPastSurgicalHistoryDetails("");
  }

 // Set past oncology history
if (data.pastOncologyHistories && data.pastOncologyHistories.length > 0) {
  console.log("Processing past oncology history:", data.pastOncologyHistories);
  const oncologyData = data.pastOncologyHistories[0];
  
  // Get raw status from API - check all possible field names
  let apiStatus = oncologyData.oncologyHistoryStatus || 
                 oncologyData.pastOncologyHistoryStatus || 
                 oncologyData.status || "";
  
  console.log("Raw oncology history status from API:", apiStatus);
  
  // Check if there's a history value - this helps determine if it's a "Yes" case
  const hasHistory = oncologyData.history && oncologyData.history.trim() !== "";
  
  // Convert API value to display value with improved handling
  let displayStatus;
  
  // Direct string comparison for all known formats of "Don't Know" - CASE INSENSITIVE
  if (typeof apiStatus === 'string' && 
      (apiStatus.toUpperCase() === "DONT_KNOW" || 
       apiStatus.toUpperCase() === "DON'T KNOW" || 
       apiStatus.toUpperCase() === "D")) {
    displayStatus = "Don't Know";
    console.log("Setting to Don't Know - matched pattern:", apiStatus);
    
    // Store the original status to ensure it's preserved
    window.localStorage.setItem('pastOncologyHistoryStatus', "Don't Know");
  } 
  // Direct string comparison for all known formats of "No Past Oncology History"
  else if (typeof apiStatus === 'string' && 
           (apiStatus.toUpperCase() === "NO_ONCOLOGY_HISTORY" || 
            apiStatus.toUpperCase() === "NO PAST ONCOLOGY HISTORY" || 
            apiStatus.toUpperCase() === "N")) {
    displayStatus = "No Past Oncology History";
    console.log("Setting to No Past Oncology History - matched pattern:", apiStatus);
    
    // Store the original status to ensure it's preserved
    window.localStorage.setItem('pastOncologyHistoryStatus', "No Past Oncology History");
  } 
  // Direct string comparison for all known formats of "Yes"
  else if (typeof apiStatus === 'string' && 
           (apiStatus.toUpperCase() === "YES" || 
            apiStatus.toUpperCase() === "Y")) {
    displayStatus = "Yes";
    console.log("Setting to Yes - matched pattern:", apiStatus);
    
    // Store the original status to ensure it's preserved
    window.localStorage.setItem('pastOncologyHistoryStatus', "Yes");
  } 
  // If it has a history but status is ambiguous, set to "Yes" 
  else if (hasHistory) {
    displayStatus = "Yes";
    console.log("Setting to Yes based on history content");
    
    // Store the original status to ensure it's preserved
    window.localStorage.setItem('pastOncologyHistoryStatus', "Yes");
  } 
  // If we can't determine the status, don't default to any option
  else {
    // Check if we have a stored value from previous interactions
    const storedStatus = window.localStorage.getItem('pastOncologyHistoryStatus');
    if (storedStatus && (storedStatus === "Don't Know" || storedStatus === "No Past Oncology History" || storedStatus === "Yes")) {
      displayStatus = storedStatus;
      console.log("Using stored status from localStorage:", storedStatus);
    } else {
      // Log the fallback case very explicitly for debugging
      console.log("WARNING: Could not determine oncology history status from:", apiStatus);
      console.log("Not setting any default option");
      displayStatus = "";
      
      // Clear any previously stored value
      window.localStorage.removeItem('pastOncologyHistoryStatus');
    }
  }
  
  console.log("FINAL oncology history display status:", displayStatus);
  
  // Set the state directly without any delay or race condition
  setPastOncologyHistory(displayStatus);
  
  // Then set the details based on the status
  if (displayStatus === 'Yes') {
    setPastOncologyHistoryDetails(oncologyData.history || oncologyData.details || "");
    console.log("Setting oncology details to:", oncologyData.history || oncologyData.details);
  } else {
    setPastOncologyHistoryDetails("");
  }
} else {
  // For new patients, don't select any option by default
  console.log("No oncology history data found, setting to empty for new patients");
  setPastOncologyHistory("");
  // Clear any previously stored value
  window.localStorage.removeItem('pastOncologyHistoryStatus');
  setPastOncologyHistoryDetails("");
}

  // Set reproductive history
  if (data.reproductiveHistory) {
    const repHistory = data.reproductiveHistory;
    setMenarche(repHistory.menarcheAge ? repHistory.menarcheAge.toString() : '');
    setPeriods(repHistory.periodsType || '');
    setBleeding(repHistory.bleedingType || '');
    setMenopausalStatus(repHistory.menopausalStatus || '');
    setMaritalStatus(repHistory.maritalStatus || '');
    setChildren(repHistory.hasChildren || '');
    setNumberOfChildren(repHistory.numberOfChildren ? repHistory.numberOfChildren.toString() : '');
    setAgeAtFirstBirth(repHistory.ageAtFirstBirth ? repHistory.ageAtFirstBirth.toString() : '');
    setBreastFeed(repHistory.breastfed || '');
  }
};

  // Function to handle row deletion from tables
  const handleRowDelete = (tableType, rowIndex, rowId) => {
    // If the row has an ID (exists in the database), add it to deletedRows
    if (rowId) {
      setDeletedRowsTracking(prevState => {
        const newState = { ...prevState };
        
        switch(tableType) {
          case 'addiction':
            newState.deletedAddictionTable = [...newState.deletedAddictionTable, rowId];
            break;
          case 'allergies':
            newState.deletedAllergiesTable = [...newState.deletedAllergiesTable, rowId];
            break;
          case 'family':
            newState.deletedFamilyTable = [...newState.deletedFamilyTable, rowId];
            break;
          case 'medication':
            newState.deletedMedicationTable = [...newState.deletedMedicationTable, rowId];
            break;
          case 'comorbidity':
            newState.deletedComorbidityTable = [...newState.deletedComorbidityTable, rowId];
            break;
          default:
            break;
        }
        
        return newState;
      });
    }
    
    // Remove the row from the UI
    switch(tableType) {
      case 'addiction':
        setAddictionTable(prevTable => {
          const newTable = [...prevTable];
          newTable.splice(rowIndex, 1);
          // Ensure there's always at least one empty row
          if (newTable.length === 0) {
            newTable.push({ id: null, historyOfAddiction: '', startedAtAge: '', perDay: '', duration: '', reformade: '', comments: '' });
          }
          return newTable;
        });
        break;
      case 'allergies':
        setAllergiesTable(prevTable => {
          const newTable = [...prevTable];
          newTable.splice(rowIndex, 1);
          // Ensure there's always at least one empty row
          if (newTable.length === 0) {
            newTable.push({ id: null, allergy: '', allergyType: '', duration: '', comment: '' });
          }
          return newTable;
        });
        break;
      case 'family':
        setFamilyTable(prevTable => {
          const newTable = [...prevTable];
          newTable.splice(rowIndex, 1);
          // Ensure there's always at least one empty row
          if (newTable.length === 0) {
            newTable.push({ id: null, historyOf: '', typeOfRelation: '', atTheAgeOf: '', specification: '', comment: '' });
          }
          return newTable;
        });
        break;
      case 'medication':
        setMedicationTable(prevTable => {
          const newTable = [...prevTable];
          newTable.splice(rowIndex, 1);
          // Ensure there's always at least one empty row
          if (newTable.length === 0) {
            newTable.push({ id: null, medicine: '', dose: '', when: '', duration: '', instructions: '' });
          }
          return newTable;
        });
        break;
      case 'comorbidity':
        setComorbidityTable(prevTable => {
          const newTable = [...prevTable];
          newTable.splice(rowIndex, 1);
          // Ensure there's always at least one empty row
          if (newTable.length === 0) {
            newTable.push({ id: null, comorbidity: '', duration: '', onMedication: '' });
          }
          return newTable;
        });
        break;
      default:
        break;
    }
  };
  
  // Event handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Update the appropriate state based on the input name
    switch(name) {
      case 'menarche':
        setMenarche(value);
        break;
      case 'numberOfChildren':
        setNumberOfChildren(value);
        break;
      case 'ageAtFirstBirth':
        setAgeAtFirstBirth(value);
        break;
      case 'pastMedicalHistoryDetails':
        setPastMedicalHistoryDetails(value);
        break;
      case 'pastSurgicalHistoryDetails':
        setPastSurgicalHistoryDetails(value);
        break;
      case 'pastOncologyHistoryDetails':
        setPastOncologyHistoryDetails(value);
        break;
      // Add more cases if needed for other direct inputs
      default:
        // Handle any other inputs not explicitly listed
        break;
    }
  };

  const handleRadioChange = (field, value) => {
    // Update the appropriate state based on the field name
    switch(field) {
      case 'addictionHistory':
        setAddictionHistory(value);
        break;
      case 'allergiesHistory':
        setAllergiesHistory(value);
        break;
      case 'familyHistory':
        setFamilyHistory(value);
        break;
      case 'currentMedication':
        setCurrentMedication(value);
        break;
      case 'pastMedicalHistory':
        setPastMedicalHistory(value);
        break;
      case 'comorbidities':
        setComorbidities(value);
        break;
      case 'pastSurgicalHistory':
        setPastSurgicalHistory(value);
        break;
      case 'pastOncologyHistory':
        setPastOncologyHistory(value);
        // For Past Oncology History, also store the value in localStorage
        // to ensure it persists across page refreshes
        if (value === "Don't Know") {
          window.localStorage.setItem('pastOncologyHistoryStatus', "Don't Know");
          console.log("Stored 'Don't Know' in localStorage for pastOncologyHistory");
        } else if (value === "No Past Oncology History") {
          window.localStorage.setItem('pastOncologyHistoryStatus', "No Past Oncology History");
          console.log("Stored 'No Past Oncology History' in localStorage for pastOncologyHistory");
        } else if (value === "Yes") {
          window.localStorage.setItem('pastOncologyHistoryStatus', "Yes");
          console.log("Stored 'Yes' in localStorage for pastOncologyHistory");
        }
        break;
      case 'periods':
        setPeriods(value);
        break;
      case 'bleeding':
        setBleeding(value);
        break;
      case 'menopausalStatus':
        setMenopausalStatus(value);
        break;
      case 'maritalStatus':
        setMaritalStatus(value);
        break;
      case 'children':
        setChildren(value);
        break;
      case 'breastFeed':
        setBreastFeed(value);
        break;
      default:
        // Handle any other fields not explicitly listed
        break;
    }
  };

  // Handle changes in table data
  const handleTableChange = (tableType, index, field, value) => {
    switch(tableType) {
      case 'addictionTable':
        const updatedAddictionTable = [...addictionTable];
        updatedAddictionTable[index] = {...updatedAddictionTable[index], [field]: value};
        setAddictionTable(updatedAddictionTable);
        break;
      case 'allergiesTable':
        const updatedAllergiesTable = [...allergiesTable];
        updatedAllergiesTable[index] = {...updatedAllergiesTable[index], [field]: value};
        setAllergiesTable(updatedAllergiesTable);
        break;
      case 'familyTable':
        const updatedFamilyTable = [...familyTable];
        updatedFamilyTable[index] = {...updatedFamilyTable[index], [field]: value};
        setFamilyTable(updatedFamilyTable);
        break;
      case 'medicationTable':
        const updatedMedicationTable = [...medicationTable];
        updatedMedicationTable[index] = {...updatedMedicationTable[index], [field]: value};
        setMedicationTable(updatedMedicationTable);
        break;
      case 'comorbidityTable':
        const updatedComorbidityTable = [...comorbidityTable];
        updatedComorbidityTable[index] = {...updatedComorbidityTable[index], [field]: value};
        setComorbidityTable(updatedComorbidityTable);
        break;
      default:
        // Handle any other table types not explicitly listed
        break;
    }
  };

  // Add new row to a table
  const addTableRow = (tableType) => {
    let newRow = {};
    
    // Initialize new row based on table type
    if (tableType === 'addictionTable') {
      newRow = { historyOfAddiction: '', startedAtAge: '', perDay: '', duration: '', reformade: '', comments: '' };
      setAddictionTable([...addictionTable, newRow]);
    } else if (tableType === 'allergiesTable') {
      newRow = { allergy: '', allergyType: '', duration: '', comment: '' };
      setAllergiesTable([...allergiesTable, newRow]);
    } else if (tableType === 'familyTable') {
      newRow = { historyOf: '', typeOfRelation: '', atTheAgeOf: '', specification: '', comment: '' };
      setFamilyTable([...familyTable, newRow]);
    } else if (tableType === 'medicationTable') {
      newRow = { medicine: '', dose: '', when: '', duration: '', instructions: '' };
      setMedicationTable([...medicationTable, newRow]);
    } else if (tableType === 'comorbidityTable') {
      newRow = { comorbidity: '', duration: '', onMedication: '' };
      setComorbidityTable([...comorbidityTable, newRow]);
    }
  };

// Simplified removeTableRow function (remove the complex logic)
const removeTableRow = async (tableType, index) => {
  // Get current table data
  let currentTable;
  let setTableFunction;
  
  switch(tableType) {
    case 'addictionTable': 
      currentTable = addictionTable; 
      setTableFunction = setAddictionTable; 
      break;
    case 'allergiesTable': 
      currentTable = allergiesTable; 
      setTableFunction = setAllergiesTable; 
      break;
    case 'familyTable': 
      currentTable = familyTable; 
      setTableFunction = setFamilyTable; 
      break;
    case 'medicationTable': 
      currentTable = medicationTable; 
      setTableFunction = setMedicationTable; 
      break;
    case 'comorbidityTable': 
      currentTable = comorbidityTable; 
      setTableFunction = setComorbidityTable; 
      break;
    default: 
      console.error('Invalid table type'); 
      return;
  }

  // Check if this is the last row
  if (currentTable.length <= 1) {
    swal({
      title: "Cannot Delete",
      text: "At least one row must remain.",
      icon: "warning",
      button: "OK",
    });
    return;
  }

  // Simple confirmation
  const willRemove = await new Promise((resolve) => {
    swal({
      title: "Are you sure?",
      text: "This row will be removed.",
      icon: "warning",
      buttons: {
        cancel: "Cancel",
        confirm: {
          text: "Remove",
          visible: true,
          className: "btn-danger"
        }
      },
      dangerMode: true,
    }).then((willRemove) => {
      resolve(willRemove);
    });
  });

  if (!willRemove) {
    return;
  }

  // Get the row being removed
  const rowToRemove = currentTable[index];
  
  // If the row has an ID, track it for deletion on form submission
  if (rowToRemove.id) {
    console.log(`Tracking row with ID ${rowToRemove.id} for deletion from ${tableType}`);
    
    // Add the row ID to a deletion tracking array
    const deletedRowsKey = `deleted${tableType.charAt(0).toUpperCase() + tableType.slice(1)}`;
    setDeletedRowsTracking(prev => ({
      ...prev,
      [deletedRowsKey]: [...(prev[deletedRowsKey] || []), rowToRemove.id]
    }));
  }
  
  // Remove the row from UI
  console.log(`Removing row at index ${index} from ${tableType}`);
  
  // Remove the row from the array
  const updatedTable = currentTable.filter((_, i) => i !== index);
  
  // Ensure at least one row remains
  const finalTable = updatedTable.length === 0 
    ? [getEmptyRow(tableType)] 
    : updatedTable;
  
  setTableFunction(finalTable);
  
  // Show success message
  swal({
    title: "Removed!",
    text: "The row will be permanently deleted when you save the form.",
    icon: "success",
    button: "OK",
  });
};

// Simplified helper function to get empty row
const getEmptyRow = (tableType) => {
  const emptyRows = {
    addictionTable: { 
      id: null, 
      historyOfAddiction: '', 
      startedAtAge: '', 
      perDay: '', 
      duration: '', 
      reformade: '', 
      comments: '' 
    },
    allergiesTable: { 
      id: null, 
      allergy: '', 
      allergyType: '', 
      duration: '', 
      comment: '' 
    },
    familyTable: { 
      id: null, 
      historyOf: '', 
      typeOfRelation: '', 
      atTheAgeOf: '', 
      specification: '', 
      comment: '' 
    },
    medicationTable: { 
      id: null, 
      medicine: '', 
      dose: '', 
      when: '', 
      duration: '', 
      instructions: '' 
    },
    comorbidityTable: { 
      id: null, 
      comorbidity: '', 
      duration: '', 
      onMedication: '',
      comment: '' 
    }
  };
  
  return emptyRows[tableType] || { id: null };
};

// Enhanced function to handle table operations (add, update, deactivate)
const handleTableOperations = async (tableType) => {
  try {
    const userId = localStorage.getItem("userId") || "1";
    
    // Define API endpoints
    const baseEndpoints = {
      addictionTable: '/medicalHistory/addictionHistory',
      allergiesTable: '/medicalHistory/allergyHistory',
      familyTable: '/medicalHistory/familyHistory',
      medicationTable: '/medicalHistory/currentMedication',
      comorbidityTable: '/medicalHistory/comorbidities'
    };
    
    const baseEndpoint = baseEndpoints[tableType];
    if (!baseEndpoint) {
      console.error(`No endpoint defined for ${tableType}`);
      return false;
    }
    
    // Process each row
    for (const row of addictionTable) {
      const endpoint = baseEndpoint;
      const method = 'POST';
      
      // Prepare payload based on table type
      const payload = preparePayload(tableType, row, userId);
      
      console.log(`${method} operation for ${tableType}:`, { endpoint, payload });
      
      const response = await fetch(`${C.getUrl()}${endpoint}`, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        console.error(`Failed to ${method} record. Status: ${response.status}`);
        return false;
      }
      
      const data = await response.json();
      console.log(`${method} successful:`, data);
    }
    
    return true;
  } catch (error) {
    console.error(`Error in handleTableOperations:`, error);
    return false;
  }
};

// Function to prepare payload based on table type
const preparePayload = (tableType, row, userId) => {
  const basePayload = {
    patientId: parseInt(patientId),
    activeInd: "Y",
    createdByDTO: {
      userId: parseInt(userId)
    },
    updatedByDTO: {
      userId: parseInt(userId)
    }
  };
  
  switch(tableType) {
    case 'addictionTable':
      return {
        ...basePayload,
        addictionHistory: row.historyOfAddiction || '',
        age: parseInt(row.startedAtAge) || 0,
        perDay: row.perDay || '',
        duration: row.duration || '',
        reformade: row.reformade || '',
        comment: row.comments || ''
      };
    case 'allergiesTable':
      return {
        ...basePayload,
        allergyHistory: row.allergy || '',
        allergyType: row.allergyType || '',
        duration: row.duration || '',
        comment: row.comment || ''
      };
    case 'familyTable':
      return {
        ...basePayload,
        history: row.historyOf || '',
        relation: row.typeOfRelation || '',
        age: parseInt(row.atTheAgeOf) || 0,
        specification: row.specification || '',
        comment: row.comment || ''
      };
    case 'medicationTable':
      return {
        ...basePayload,
        medicine: row.medicine || '',
        dose: row.dose || '',
        when: row.when || '',
        duration: row.duration || '',
        instruction: row.instructions || ''
      };
    case 'comorbidityTable':
      return {
        ...basePayload,
        comorbidity: row.comorbidity || '',
        duration: row.duration || '',
        medication: row.onMedication || 'No',
        comment: row.comment || ''
      };
    default:
      return basePayload;
  };
};

// Function to validate the form with improved flexibility
const validateForm = () => {
  const newErrors = {};
  
  // Log validation start for debugging
  console.log('Starting form validation');
  
  // No longer requiring all fields to be filled out
  // Only validate fields that have "Yes" selected
  
  // For Addiction History - only validate if 'Yes' is selected
  if (addictionHistory === 'Yes') {
    const hasValidAddictionData = addictionTable.some(row => 
      row.historyOfAddiction && row.historyOfAddiction.trim() !== ''
    );
    
    if (!hasValidAddictionData) {
      newErrors.addictionTable = 'Addiction history details are required when Yes is selected';
    }
  }
  // No validation needed for 'No' or 'Don't Know' selections

  // For Allergies History - only validate if 'Yes' is selected
  if (allergiesHistory === 'Yes') {
    const hasValidAllergyData = allergiesTable.some(row => 
      row.allergy && row.allergy.trim() !== ''
    );
    
    if (!hasValidAllergyData) {
      newErrors.allergiesTable = 'Allergy details are required when Yes is selected';
    }
  }
  // No validation needed for 'No' or 'Don't Know' selections

  // For Family History - only validate if 'Yes' is selected
  if (familyHistory === 'Yes') {
    const hasValidFamilyData = familyTable.some(row => 
      row.historyOf && row.historyOf.trim() !== ''
    );
    
    if (!hasValidFamilyData) {
      newErrors.familyTable = 'Family history details are required when Yes is selected';
    }
  }
  // No validation needed for 'No' or 'Don't Know' selections

  // For Current Medication - only validate if 'Yes' is selected
  if (currentMedication === 'Yes') {
    const hasValidMedicationData = medicationTable.some(row => 
      row.medicine && row.medicine.trim() !== ''
    );
    
    if (!hasValidMedicationData) {
      newErrors.medicationTable = 'Medication details are required when Yes is selected';
    }
  }
  // No validation needed for 'No' or 'Don't Know' selections

  // For Past Medical History - only validate if 'Yes' is selected
  if (pastMedicalHistory === 'Yes' && (!pastMedicalHistoryDetails || pastMedicalHistoryDetails.trim() === '')) {
    newErrors.pastMedicalHistoryDetails = 'Medical history details are required when Yes is selected';
  }
  // No validation needed for 'No' or 'Don't Know' selections

  // For Comorbidities - only validate if 'Yes' is selected
  if (comorbidities === 'Yes') {
    const hasValidComorbidityData = comorbidityTable.some(row => 
      row.comorbidity && row.comorbidity.trim() !== ''
    );
    
    if (!hasValidComorbidityData) {
      newErrors.comorbidityTable = 'Comorbidity details are required when Yes is selected';
    }
  }
  // No validation needed for 'No' or 'Don't Know' selections

  // For Past Surgical History - only validate if 'Yes' is selected
  if (pastSurgicalHistory === 'Yes' && (!pastSurgicalHistoryDetails || pastSurgicalHistoryDetails.trim() === '')) {
    newErrors.pastSurgicalHistoryDetails = 'Surgical history details are required when Yes is selected';
  }
  // No validation needed for 'No' or 'Don't Know' selections

  // For Past Oncology History - only validate if 'Yes' is selected
  if (pastOncologyHistory === 'Yes' && (!pastOncologyHistoryDetails || pastOncologyHistoryDetails.trim() === '')) {
    newErrors.pastOncologyHistoryDetails = 'Oncology history details are required when Yes is selected';
  }
  // No validation needed for 'No' or 'Don't Know' selections

  // For Reproductive History - more flexible validation based on gender
  // Making all reproductive history fields optional unless explicitly required
  if (patientGender === 'F') {
    // Only validate children-related fields if 'children' is set to 'Yes'
    if (children === 'Yes') {
      if (!numberOfChildren && document.getElementById('numberOfChildren')) {
        newErrors.numberOfChildren = 'Number of children is required when Yes is selected';
      }
      if (!ageAtFirstBirth && document.getElementById('ageAtFirstBirth')) {
        newErrors.ageAtFirstBirth = 'Age at first birth is required when Yes is selected';
      }
      if (!breastFeed && document.getElementById('breastFeed')) {
        newErrors.breastFeed = 'Breast feed information is required when Yes is selected';
      }
    }
    // No validation for other reproductive fields unless explicitly needed
  } else if (patientGender === 'M') {
    // For male patients, only validate children-related fields if 'Yes' is selected
    if (children === 'Yes' && !numberOfChildren && document.getElementById('numberOfChildren')) {
      newErrors.numberOfChildren = 'Number of children is required when Yes is selected';
    }
    // No validation for other fields
  }
  
  // Set errors and log validation results
  setErrors(newErrors);
  const isValid = Object.keys(newErrors).length === 0;
  console.log('Form validation result:', isValid ? 'Valid' : 'Invalid', newErrors);
  return isValid;
  };

const formatMedicalHistoryPayload = () => {
  const userId = localStorage.getItem("userId"); // Get userId from localStorage
  
  // Create payload structure
    const payload = {
      addictionHistories: [],
      allergies: [],
      familyHistories: [],
      pastMedicalHistories: [],
      pastOncologyHistories: [],
      currentMedications: [],
      comorbidities: [],
      pastSurgicalHistories: [],
      reproductiveHistory: null
    };
    
    // Helper function to convert UI-friendly labels to API enum values
    const getApiValue = (value, type) => {
      // Map UI labels to backend enum values
      const typeMap = {
        'addiction': {
          'No History of Addiction': 'NO_HISTORY_OF_ADDICTION',
          "Don't Know": 'DONT_KNOW',
          'Yes': 'Yes'
        },
        'allergy': {
          'No History of Allergies': 'NO_HISTORY_OF_ALLERGY',
          "Don't Know": 'DONT_KNOW',
          'Yes': 'Yes'
        },
        'family': {
          'No History of Family': 'NO_HISTORY_OF_FAMILY_CONDITION',
          "Don't Know": 'DONT_KNOW',
          'Yes': 'Yes'
        },
        'medication': {
          'No Current Medication': 'NO_CURRENT_MEDICATION',
          "Don't Know": 'DONT_KNOW',
          'Yes': 'Yes'
        },
        'medical': {
          'No Past Medical History': 'NO_MEDICAL_HISTORY',
          "Don't Know": 'DONT_KNOW',
          'Yes': 'Yes'
        },
        'comorbidity': {
          'No History of Comorbidities': 'NO_COMORBIDITIES',
          "Don't Know": 'DONT_KNOW',
          'Yes': 'Yes'
        },
        'surgical': {
          'No Past Surgical History': 'NO_SURGICAL_HISTORY',
          "Don't Know": 'DONT_KNOW',
          'Yes': 'Yes'
        },
        'oncology': {
          'No Past Oncology History': 'NO_ONCOLOGY_HISTORY',
          "Don't Know": 'DONT_KNOW',
          'Yes': 'Yes'
        }
      };
      
      return typeMap[type][value] || value;
    };
    
    // Helper function to find addiction ID by name
    const getAddictionMasterId = (addictionName) => {
      const addiction = fetchedAddictionTypeMasterData.find(item => item.addiction === addictionName);
      return addiction ? addiction.id : null;
    };
    
    // Format addiction histories - with added addictionMasterId field
    if (addictionHistory) {
      const statusValue = getApiValue(addictionHistory, 'addiction');
      
      // Add a default entry for ANY addiction status (Yes, DONT_KNOW, or NO_HISTORY_OF_ADDICTION)
      const addictionEntry = {
        patientId: parseInt(patientId),
        activeInd: "Y",
        addictionStatus: statusValue,
        createdByDTO: {
          userId: parseInt(userId)
        },
        updatedByDTO: {
          userId: parseInt(userId)
        }
      };
      
      // Add additional fields only for "Yes" status
      if (statusValue === 'Yes') {
        // Check if there are valid table entries to include
        const validEntries = addictionTable.filter(item => item.historyOfAddiction);
        
        if (validEntries.length > 0) {
          // Use the valid entries
          payload.addictionHistories = validEntries.map(item => {
            // Find the addiction master ID for this addiction type
            const addictionMasterId = getAddictionMasterId(item.historyOfAddiction);
            
            return {
              patientId: parseInt(patientId),
              activeInd: "Y",
              addictionStatus: statusValue,
              addictionHistory: item.historyOfAddiction || 'Not specified',
              addictionMasterId: addictionMasterId, // Add the addiction master ID
              age: parseInt(item.startedAtAge) || 0,
              perDay: item.perDay || '',
              duration: item.duration || '',
              reformade: item.reformade || '',
              comment: item.comments || '',
              createdByDTO: {
                userId: parseInt(userId)
              },
              updatedByDTO: {
                userId: parseInt(userId)
              }
            };
          });
        } else {
          // No valid entries, use a placeholder
          addictionEntry.addictionHistory = "Not specified";
          addictionEntry.age = 0;
          addictionEntry.perDay = '';
          addictionEntry.duration = '';
          addictionEntry.comment = '';
          // Don't set addictionMasterId for placeholder
          payload.addictionHistories.push(addictionEntry);
        }
      } else {
        // For "No History" and "Don't Know" options, just push the basic entry
        payload.addictionHistories.push(addictionEntry);
      }
    }
    
    // Format allergies
    if (allergiesHistory) {
      const statusValue = getApiValue(allergiesHistory, 'allergy');
      
      if (statusValue === 'Yes' && allergiesTable.length > 0) {
        // Filter out blank rows - only include rows where allergy field has a value
        const nonEmptyRows = allergiesTable.filter(item => item.allergy && item.allergy.trim() !== '');
        
        if (nonEmptyRows.length > 0) {
          payload.allergies = nonEmptyRows.map(item => ({
            allergyHistory: item.allergy || '',
            allergy_Type: item.allergyType || item.allergy_Type || '',
            allergyType: item.allergyType || item.allergy_Type || '',
            duration: item.duration || '',
            comment: item.comment || '',
            patientId: parseInt(patientId),
            activeInd: "Y",
            allergyStatus: statusValue,
            createdByDTO: {
              userId: parseInt(userId)
            },
            updatedByDTO: {
              userId: parseInt(userId)
            }
          }));
        }
      } else {
        // For "No History" and "Don't Know" options
        payload.allergies.push({
          patientId: parseInt(patientId),
          activeInd: "Y",
          allergyStatus: statusValue,
          createdByDTO: {
            userId: parseInt(userId)
          },
          updatedByDTO: {
            userId: parseInt(userId)
          }
        });
      }
    }
    
    // Format family histories
    if (familyHistory) {
      const statusValue = getApiValue(familyHistory, 'family');
      
      if (statusValue === 'Yes' && familyTable.length > 0) {
        // Filter out blank rows - only include rows where historyOf field has a value
        const nonEmptyRows = familyTable.filter(item => item.historyOf && item.historyOf.trim() !== '');
        
        if (nonEmptyRows.length > 0) {
          payload.familyHistories = nonEmptyRows.map(item => ({
            history: item.historyOf || '', 
            relation: item.typeOfRelation || '',
            age: item.atTheAgeOf ? parseInt(item.atTheAgeOf) : 0,
            specification: item.specification || '',
            comment: item.comment || '',
            patientId: parseInt(patientId),
            activeInd: "Y",
            familyHistoryStatus: statusValue,
            createdByDTO: {
              userId: parseInt(userId)
            },
            updatedByDTO: {
              userId: parseInt(userId)
            }
          }));
        }
      } else {
        // For "No History" and "Don't Know" options
        payload.familyHistories.push({
          patientId: parseInt(patientId),
          activeInd: "Y",
          familyHistoryStatus: statusValue,
          createdByDTO: {
            userId: parseInt(userId)
          },
          updatedByDTO: {
            userId: parseInt(userId)
          }
        });
      }
    }
    
    // Format current medications
    if (currentMedication) {
      const statusValue = getApiValue(currentMedication, 'medication');
      
      if (statusValue === 'Yes' && medicationTable.length > 0) {
        // Filter out blank rows - only include rows where medicine field has a value
        const nonEmptyRows = medicationTable.filter(item => item.medicine && item.medicine.trim() !== '');
        
        if (nonEmptyRows.length > 0) {
          payload.currentMedications = nonEmptyRows.map(item => ({
            medicine: item.medicine || '',
            dose: item.dose || '',
            when: item.when || '',
            duration: item.duration || '',
            instruction: item.instructions || '',
            patientId: parseInt(patientId),
            activeInd: "Y",
            medicationStatus: statusValue,
            createdByDTO: {
              userId: parseInt(userId)
            },
            updatedByDTO: {
              userId: parseInt(userId)
            }
          }));
        }
      } else {
        // For "No Current Medication" and "Don't Know" options
        payload.currentMedications.push({
          patientId: parseInt(patientId),
          activeInd: "Y",
          medicationStatus: statusValue,
          createdByDTO: {
            userId: parseInt(userId)
          },
          updatedByDTO: {
            userId: parseInt(userId)
          }
        });
      }
    }
    
    // Format past medical history
    if (pastMedicalHistory) {
      const statusValue = getApiValue(pastMedicalHistory, 'medical');
      
      payload.pastMedicalHistories.push({
        history: statusValue === 'Yes' ? (pastMedicalHistoryDetails || '') : '',
        patientId: parseInt(patientId),
        activeInd: "Y",
        medicalHistoryStatus: statusValue,
        createdByDTO: {
          userId: parseInt(userId)
        },
        updatedByDTO: {
          userId: parseInt(userId)
        }
      });
    }
    
    // Format comorbidities
    if (comorbidities) {
      const statusValue = getApiValue(comorbidities, 'comorbidity');
      
      if (statusValue === 'Yes' && comorbidityTable.length > 0) {
        // Filter out blank rows - only include rows where comorbidity field has a value
        const nonEmptyRows = comorbidityTable.filter(item => item.comorbidity && item.comorbidity.trim() !== '');
        
        if (nonEmptyRows.length > 0) {
          payload.comorbidities = nonEmptyRows.map(item => ({
            comorbidity: item.comorbidity || '',
            duration: item.duration || '',
            medication: item.onMedication === 'Yes' ? 'Yes' : 'No',
            comment: item.comment || '',
            patientId: parseInt(patientId),
            activeInd: "Y",
            comorbidityStatus: statusValue,
            createdByDTO: {
              userId: parseInt(userId)
            },
            updatedByDTO: {
              userId: parseInt(userId)
            }
          }));
        }
      } else {
        // For "No History" and "Don't Know" options
        payload.comorbidities.push({
          patientId: parseInt(patientId),
          activeInd: "Y",
          comorbidityStatus: statusValue,
          createdByDTO: {
            userId: parseInt(userId)
          },
          updatedByDTO: {
            userId: parseInt(userId)
          }
        });
      }
    }
    
    // Format past surgical history
    if (pastSurgicalHistory) {
      const statusValue = getApiValue(pastSurgicalHistory, 'surgical');
      
      payload.pastSurgicalHistories.push({
        history: statusValue === 'Yes' ? (pastSurgicalHistoryDetails || '') : '',
        patientId: parseInt(patientId),
        activeInd: "Y",
        surgicalHistoryStatus: statusValue,
        createdByDTO: {
          userId: parseInt(userId)
        },
        updatedByDTO: {
          userId: parseInt(userId)
        }
      });
      console.log("Adding past surgical history to payload:", statusValue, pastSurgicalHistoryDetails);
    }
    
    // Format past oncology history
    if (pastOncologyHistory) {
      // Direct mapping of display values to API values
      let statusValue;
      if (pastOncologyHistory === "Don't Know") {
        statusValue = "DONT_KNOW";
      } else if (pastOncologyHistory === "No Past Oncology History") {
        statusValue = "NO_ONCOLOGY_HISTORY";
      } else if (pastOncologyHistory === "Yes") {
        statusValue = "Yes";
      } else {
        // Fallback to the original mapping function if needed
        statusValue = getApiValue(pastOncologyHistory, 'oncology');
      }
      
      // Log the conversion for verification
      console.log(`Converting oncology display value "${pastOncologyHistory}" to API value "${statusValue}"`);
      
      // Create a more complete payload for oncology history
      const oncologyPayload = {
        history: statusValue === 'Yes' ? (pastOncologyHistoryDetails || '') : '',
        patientId: parseInt(patientId),
        activeInd: "Y",
        oncologyHistoryStatus: statusValue,
        pastOncologyHistoryStatus: statusValue, // Add this field as well for redundancy
        status: statusValue, // Add this field as well for redundancy
        createdByDTO: {
          userId: parseInt(userId)
        },
        updatedByDTO: {
          userId: parseInt(userId)
        }
      };
      
      if (!payload.pastOncologyHistories) {
        payload.pastOncologyHistories = [];
      }
      payload.pastOncologyHistories.push(oncologyPayload);
      console.log("Adding past oncology history to payload:", oncologyPayload);
    }

    // Format reproductive history if any field is set
    if (menarche || periods || bleeding || 
        menopausalStatus || maritalStatus || children) {
      payload.reproductiveHistory = {
        menarcheAge: parseInt(menarche) || 0,
        periodsType: periods || '',
        bleedingType: bleeding || '',
        menopausalStatus: menopausalStatus || '',
        maritalStatus: maritalStatus || '',
        hasChildren: children || '',
        numberOfChildren: parseInt(numberOfChildren) || 0,
        ageAtFirstBirth: parseInt(ageAtFirstBirth) || 0,
        breastfed: breastFeed || '',
        patientId: parseInt(patientId),
        activeInd: "Y",
        reproductiveHistoryStatus: "Yes",
        createdByDTO: {
          userId: parseInt(userId)
        },
        updatedByDTO: {
          userId: parseInt(userId)
        }
      };
    }
    
    console.log("Payload being sent:", payload); // Add this for debugging
    return payload;
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset any previous errors
    setErrors({});
    
    // Validate the form with more detailed error reporting
    if (!validateForm()) {
      console.log('Form validation failed. Errors:', errors);
      // Show error message to the user
      swal({
      title: "Validation Error",
      text: "Please fill in all required fields correctly before submitting.",
      icon: "warning",
      button: "OK",
    });
    return;
  }

  // Prevent multiple submissions
  if (saveLoader) {
    console.log('Submit already in progress, preventing duplicate submission');
    return;
  }

  setSaveLoader(true);
  console.log('Starting form submission process');

  try {
    // Step 1: Handle deletions first
    console.log('Processing deletions:', deletedRowsTracking);
    
    // Process deletions with error handling
    const deletionPromises = [];
    
    // Delete addiction records
    for (const id of deletedRowsTracking.deletedAddictionTable) {
      deletionPromises.push(deleteRecord('addictionTable', id).catch(err => {
        console.warn(`Failed to delete addiction record ${id}:`, err);
        // Continue with other deletions even if one fails
      }));
    }
    
    // Delete allergy records
    for (const id of deletedRowsTracking.deletedAllergiesTable) {
      deletionPromises.push(deleteRecord('allergiesTable', id).catch(err => {
        console.warn(`Failed to delete allergy record ${id}:`, err);
      }));
    }
    
    // Delete family history records
    for (const id of deletedRowsTracking.deletedFamilyTable) {
      deletionPromises.push(deleteRecord('familyTable', id).catch(err => {
        console.warn(`Failed to delete family history record ${id}:`, err);
      }));
    }
    
    // Delete medication records
    for (const id of deletedRowsTracking.deletedMedicationTable) {
      deletionPromises.push(deleteRecord('medicationTable', id).catch(err => {
        console.warn(`Failed to delete medication record ${id}:`, err);
      }));
    }
    
    // Delete comorbidity records
    for (const id of deletedRowsTracking.deletedComorbidityTable) {
      deletionPromises.push(deleteRecord('comorbidityTable', id).catch(err => {
        console.warn(`Failed to delete comorbidity record ${id}:`, err);
      }));
    }
    
    // Wait for all deletions to complete (or fail) before proceeding
    await Promise.allSettled(deletionPromises);
    console.log('Deletion processing completed');
    
    // Step 2: Format and save the remaining data
    const payload = formatMedicalHistoryPayload();
    
    console.log("Submitting payload:", JSON.stringify(payload, null, 2));
    
    const response = await fetch(`${C.getUrl()}/medicalHistory/patient/${patientId}/medicalhistory`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });
    
    const responseText = await response.text();
    console.log("Raw response:", responseText);
    
    let data;
    let isSuccess = false;
    
    // Handle both JSON and non-JSON responses
    try {
      // Try to parse as JSON
      data = JSON.parse(responseText);
      console.log("Parsed response as JSON:", data);
      
      // Check for success indicators in JSON response
      isSuccess = 
        data.serviceStatus === "SUCCESS" || 
        data.status === "success" || 
        data.success === true || 
        (data.message && data.message.toLowerCase().includes("success"));
    } catch(error) {
      console.warn("Response is not valid JSON:", error);
      
      // Check if the HTML response indicates success
      // This is a fallback for when the API returns HTML instead of JSON
      if (response.ok && (responseText.toLowerCase().includes("success") || responseText.includes("200"))) {
        console.log("Non-JSON response appears to indicate success");
        isSuccess = true;
        data = { message: "Operation completed successfully" };
      } else {
        console.error("Failed to parse response and couldn't determine success status");
        data = { message: responseText.substring(0, 200) };
      }
    }
    
    // Process the response
    if (response.ok && isSuccess) {
      console.log("Operation successful");
      
      // Clear the deleted rows tracking
      setDeletedRowsTracking({
        deletedAddictionTable: [],
        deletedAllergiesTable: [],
        deletedFamilyTable: [],
        deletedMedicationTable: [],
        deletedComorbidityTable: []
      });
      
      swal({
        title: "Success",
        text: "Medical history updated successfully!",
        icon: "success",
        button: "OK",
      });
      
      setShowEditForm(false);
      // Refresh the data
      fetchAllMedicalHistory(patientId);
    } else {
      // Handle error response
      console.error("Response indicates failure:", response.status, data);
      throw new Error(`Failed to update medical history: ${response.status} ${JSON.stringify(data)}`); 
    }
  } catch (error) {
    console.error('Error saving medical history:', error);
    swal({
      title: "Error",
      text: `An error occurred while saving medical history: ${error.message}`,
      icon: "error",
      button: "OK",
    });
  } finally {
    // Always reset the save loader state to prevent the button from staying disabled
    setSaveLoader(false);
    console.log('Form submission process completed, saveLoader reset to false');
  }
};

// Function to delete a specific record
const deleteRecord = async (tableType, rowId) => {
  try {
    const userId = localStorage.getItem("userId") || "1";
    
    const endpoints = {
      addictionTable: `/medicalHistory/addictionHistory/${rowId}`,
      allergiesTable: `/medicalHistory/allergyHistory/${rowId}`,
      familyTable: `/medicalHistory/familyHistory/${rowId}`,
      medicationTable: `/medicalHistory/currentMedication/${rowId}`,
      comorbidityTable: `/medicalHistory/comorbidities/${rowId}`,
      pastMedicalHistory: `/medicalHistory/pastMedicalHistory/${rowId}`,
      pastSurgicalHistory: `/medicalHistory/pastSurgicalHistory/${rowId}`,
      pastOncologyHistory: `/medicalHistory/pastOncologyHistory/${rowId}`
    };
    
    console.log(`Deleting ${tableType} record with ID: ${rowId} using endpoint: ${endpoints[tableType]}`);
    
    const endpoint = endpoints[tableType];
    if (!endpoint) {
      console.error(`No endpoint defined for ${tableType}`);
      return false;
    }
    
    console.log(`Deleting ${tableType} record with ID: ${rowId}`);
    
    // First try to mark the record as inactive using PUT method
    try {
      const inactiveResponse = await fetch(`${C.getUrl()}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          activeInd: "N",
          updatedByDTO: {
            userId: parseInt(userId)
          }
        })
      });
      
      if (inactiveResponse.ok) {
        const data = await inactiveResponse.json();
        console.log(`Successfully marked ${tableType} record as inactive:`, data);
        return true;
      }
    } catch (putError) {
      console.error(`Error marking record as inactive: ${putError}`);
      // Continue to try DELETE method as fallback
    }
    
    // If marking as inactive fails, try using DELETE method as fallback
    const response = await fetch(`${C.getUrl()}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      }
    });
    
    if (!response.ok) {
      console.error(`Failed to delete record. Status: ${response.status}`);
      return false;
    }
    
    const data = await response.json();
    console.log(`Successfully deleted ${tableType} record:`, data);
    
    return true;
  } catch (error) {
    console.error(`Error deleting ${tableType} record:`, error);
    return false;
  }
};

  // Rendering functions
  const renderMedicalHistoryView = () => {
    // Style for the boxes around each section
    const sectionBoxStyle = {
      border: '1px solid #ddd',
      borderRadius: '8px',
      padding: '15px',
      marginBottom: '20px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      backgroundColor: '#fff',
      paddingRight: '20px'
    };

    const sectionTitleStyle = {
      borderBottom: '2px solid #31B492',
      paddingBottom: '10px',
      marginBottom: '15px',
      color: '#31B492',
      fontWeight: '600'
    };
    return (
      <Container fluid className="p-0 mt-4" style={{ paddingLeft: '20px', paddingRight: '20px' }}>
        {patientData && renderPatientInfo()}
        
        <div className="medical-history-header mb-4">
          <Button variant="secondary" className="mr-3" onClick={toggleEditForm}>
            Edit
          </Button>
        </div>
  
        <div className="medical-history-content">
          {/* Addiction History */}
          <div className="history-section mb-3" style={sectionBoxStyle}>
            <h4 className="history-title" style={sectionTitleStyle}>Addiction History</h4>
            <div className="history-data">
              {addictionHistory === 'Yes' && addictionTable.length > 0 && addictionTable.some(row => row.historyOfAddiction) ? (
                <div>
                  <table className="table table-bordered" style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>HISTORY OF ADDICTION</th>
                        <th>STARTED AT THE AGE OF (YEARS)</th>
                        <th>PER DAY</th>
                        <th>DURATION</th>
                        <th>REFORMED</th>
                        <th>COMMENTS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {addictionTable.filter(row => row.historyOfAddiction).map((row, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{row.historyOfAddiction}</td>
                          <td>{row.startedAtAge}</td>
                          <td>{row.perDay}</td>
                          <td>{row.duration}</td>
                          <td>{row.reformade}</td>
                          <td>{row.comments}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                // Only show status for "No History" or "Don't Know" options
                <p>{addictionHistory || 'No data'}</p>
              )}
            </div>
          </div>
  
          {/* History of Allergies */}
          <div className="history-section mb-3" style={sectionBoxStyle}>
            <h4 className="history-title" style={sectionTitleStyle}>History of Allergies</h4>
            <div className="history-data">
              {allergiesHistory === 'Yes' && allergiesTable.length > 0 && allergiesTable.some(row => row.allergy) ? (
                <div>
                  <table className="table table-bordered" style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>ALLERGY</th>
                        <th>ALLERGY TYPE</th>
                        <th>DURATION</th>
                        <th>COMMENT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allergiesTable.filter(row => row.allergy).map((row, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{row.allergy}</td>
                          <td>{row.allergyType}</td>
                          <td>{row.duration}</td>
                          <td>{row.comment}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>{allergiesHistory || 'No data'}</p>
              )}
            </div>
          </div>
  
          {/* Current Medication */}
          <div className="history-section mb-3" style={sectionBoxStyle}>
            <h4 className="history-title" style={sectionTitleStyle}>Current Medication</h4>
            <div className="history-data">
              {currentMedication === 'Yes' && medicationTable.length > 0 && medicationTable.some(row => row.medicine) ? (
                <div>
                  <table className="table table-bordered" style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                    <thead>
                      <tr>
                        <th>MEDICINE</th>
                        <th>DOSE</th>
                        <th>WHEN</th>
                        <th>DURATION</th>
                        <th>INSTRUCTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {medicationTable.filter(row => row.medicine).map((row, index) => (
                        <tr key={index}>
                          <td>{row.medicine}</td>
                          <td>{row.dose}</td>
                          <td>{row.when}</td>
                          <td>{row.duration}</td>
                          <td>{row.instructions}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>{currentMedication || 'No data'}</p>
              )}
            </div>
          </div>
  
          {/* Family History */}
          <div className="history-section mb-3" style={sectionBoxStyle}>
            <h4 className="history-title" style={sectionTitleStyle}>Family History</h4>
            <div className="history-data">
              {familyHistory === 'Yes' && familyTable.length > 0 && familyTable.some(row => row.historyOf) ? (
                <div>
                  <table className="table table-bordered" style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>HISTORY OF</th>
                        <th>TYPE OF RELATION</th>
                        <th>AT THE AGE OF (YEARS)</th>
                        <th>SPECIFICATION</th>
                        <th>COMMENT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {familyTable.filter(row => row.historyOf).map((row, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{row.historyOf}</td>
                          <td>{row.typeOfRelation}</td>
                          <td>{row.atTheAgeOf}</td>
                          <td>{row.specification}</td>
                          <td>{row.comment}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>{familyHistory || 'No data'}</p>
              )}
            </div>
          </div>
  
          {/* Past Medical History */}
          <div className="history-section mb-3" style={sectionBoxStyle}>
            <h4 className="history-title" style={sectionTitleStyle}>Past Medical History</h4>
            <div className="history-data">
              {console.log("Medical History Status Type:", typeof pastMedicalHistory)}
              {console.log("Medical History Status Value:", `"${pastMedicalHistory}"`)}
              {pastMedicalHistory === 'Yes' ? (
                <div style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                  <p>{pastMedicalHistoryDetails || 'No details provided'}</p>
                </div>
              ) : (
                <div style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                  <p>{pastMedicalHistory ||'No data'}</p>
                </div>
              )}
            </div>
          </div>
  
          {/* Comorbidities */}
          <div className="history-section mb-3" style={sectionBoxStyle}>
            <h4 className="history-title" style={sectionTitleStyle}>Comorbidities</h4>
            <div className="history-data">
              {comorbidities === 'Yes' && comorbidityTable.length > 0 && comorbidityTable.some(row => row.comorbidity) ? (
                <div>
                  <table className="table table-bordered" style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>COMORBIDITY</th>
                        <th>DURATION</th>
                        <th>ON MEDICATION</th>
                        <th>COMMENT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comorbidityTable.filter(row => row.comorbidity).map((row, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{row.comorbidity}</td>
                          <td>{row.duration}</td>
                          <td>{row.onMedication || 'No'}</td>
                          <td>{row.comment}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>{comorbidities || 'No data'}</p>
              )}
            </div>
          </div>
          
          {/* Past Surgical History */}
          <div className="history-section mb-3" style={sectionBoxStyle}>
            <h4 className="history-title" style={sectionTitleStyle}>Past Surgical History</h4>
            <div className="history-data">
              {pastSurgicalHistory === 'Yes' ? (
                <div style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                  <p>{pastSurgicalHistoryDetails || 'No details provided'}</p>
                </div>
              ) : (
                <div style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                  <p>{pastSurgicalHistory ||'No data'}</p>
                </div>
              )}
            </div>
          </div>

  
          {/* Reproductive History - Display based on gender */}
          {(patientGender === 'Female' && (menarche || periods || bleeding || menopausalStatus || maritalStatus || children || 
            numberOfChildren || ageAtFirstBirth || breastFeed)) || 
            (patientGender === 'Male' && (maritalStatus || children || numberOfChildren)) ? (
            <div className="history-section mb-3" style={sectionBoxStyle}>
              <h4 className="history-title" style={sectionTitleStyle}>Reproductive History</h4>
              <div className="history-data">
                <table className="table table-bordered">
                  <tbody>
                    {/* Female-specific fields */}
                    {patientGender === 'Female' && (
                      <>
                        {menarche && (
                          <tr>
                            <th>Menarche Age</th>
                            <td>{menarche}</td>
                          </tr>
                        )}
                        {periods && (
                          <tr>
                            <th>Periods</th>
                            <td>{periods}</td>
                          </tr>
                        )}
                        {bleeding && (
                          <tr>
                            <th>Bleeding</th>
                            <td>{bleeding}</td>
                          </tr>
                        )}
                        {menopausalStatus && (
                          <tr>
                            <th>Menopausal Status</th>
                            <td>{menopausalStatus}</td>
                          </tr>
                        )}
                        {ageAtFirstBirth && (
                          <tr>
                            <th>Age at 1st Birth</th>
                            <td>{ageAtFirstBirth}</td>
                          </tr>
                        )}
                        {breastFeed && (
                          <tr>
                            <th>Breast Feed</th>
                            <td>{breastFeed}</td>
                          </tr>
                        )}
                      </>
                    )}
                    
                    {/* Common fields for both male and female */}
                    {maritalStatus && (
                      <tr>
                        <th>Marital Status</th>
                        <td>{maritalStatus}</td>
                      </tr>
                    )}
                    {children && (
                      <tr>
                        <th>Children</th>
                        <td>{children}</td>
                      </tr>
                    )}
                    {numberOfChildren && (
                      <tr>
                        <th>No of Children</th>
                        <td>{numberOfChildren}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
          
          {/* Past Oncology History */}
          <div className="history-section mb-3" style={sectionBoxStyle}>
            <h4 className="history-title" style={sectionTitleStyle}>Past Oncology History</h4>
            <div className="history-data">
              {pastOncologyHistory === 'Yes' ? (
                <div style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                  <p>{pastOncologyHistoryDetails || 'No Details Provided'}</p>
                </div>
              ) : (
                <div style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                  <p>{pastOncologyHistory || 'No data'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>
    );
  };

  const renderEditForm = () => {
    // Styles for form elements
    const tableHeadingStyle = {
      fontSize: '1rem',
      fontWeight: 600
    };
    
    const radioStyle = {
      accentColor: '#28a745',  // Bootstrap's green color
      marginRight: '0.5rem'
    };
    
    const reproductiveHistoryStyle = {
      fontSize: '1rem',
      fontWeight: 600,
      marginBottom: '1rem',
      color: '#333'
    };

    return (
      <Container fluid className="p-0 mt-4" style={{ paddingLeft: '20px', paddingRight: '20px' }}>
        {patientData && renderPatientInfo()}
        
        <Form onSubmit={handleSubmit}>
          <div className="medical-history-header mb-4">
          </div>

          {/* Required medical history fields with radio buttons */}
          <div className="history-section mb-3" style={{ paddingRight: '20px' }}>
            <Form.Group className="mb-3">
              <Form.Label className="history-title">Addiction History : <span className="text-danger">*</span></Form.Label>
              <div className="d-flex align-items-center mb-2">
                <Form.Check
                  type="radio"
                  id="addiction-yes"
                  label="Yes"
                  name="addictionHistory"
                  className="mr-3"
                  checked={addictionHistory === 'Yes'}
                  onChange={() => handleRadioChange('addictionHistory', 'Yes')}
                  style={radioStyle}
                />
                <Form.Check
                  type="radio"
                  id="addiction-no"
                  label="No History of Addiction"
                  name="addictionHistory"
                  className="mx-3"
                  checked={addictionHistory === 'No History of Addiction'}
                  onChange={() => handleRadioChange('addictionHistory', 'No History of Addiction')}
                  style={radioStyle}
                />
                <Form.Check
                  type="radio"
                  id="addiction-dont-know"
                  label="Don't Know"
                  name="addictionHistory"
                  checked={addictionHistory === "Don't Know"}
                  onChange={() => handleRadioChange('addictionHistory', "Don't Know")}
                  style={radioStyle}
                />
              </div>
              {errors.addictionHistory && (
                <Form.Text className="text-danger">{errors.addictionHistory}</Form.Text>
              )}
              
              {/* Addiction History Table - Show when Yes is selected */}
              {addictionHistory === 'Yes' && (
                <div className="mt-3">
                  <table className="table table-bordered" style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                    <thead>
                      <tr>
                        <th style={tableHeadingStyle}>#</th>
                        <th style={tableHeadingStyle}>HISTORY OF ADDICTION</th>
                        <th style={tableHeadingStyle}>STARTED AT THE AGE OF (YEARS)</th>
                        <th style={tableHeadingStyle}>PER DAY</th>
                        <th style={tableHeadingStyle}>DURATION</th>
                        <th style={tableHeadingStyle}>REFORMED</th>
                        <th style={tableHeadingStyle}>COMMENTS</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {addictionTable.map((row, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                          <Form.Control
                            as="select"
                            value={row.historyOfAddiction}
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              
                              // First, update the current row with the selected value
                              const updatedTable = [...addictionTable];
                              updatedTable[index] = {...updatedTable[index], historyOfAddiction: selectedValue};
                              
                              // Then, if this is the last row and a value was selected, add a new row
                              if (index === addictionTable.length - 1 && selectedValue.trim() !== '' && selectedValue !== 'custom') {
                                updatedTable.push({ historyOfAddiction: '', startedAtAge: '', perDay: '', duration: '', reformade: '', comments: '' });
                              }
                              
                              // Update the state once with all changes
                              setAddictionTable(updatedTable);
                            }}
                          >
                            <option value="">Select addiction</option>
                            {fetchedAddictionTypeOptions.length > 0 ? (
                              fetchedAddictionTypeOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            ) : (
                              // Fallback to static options if API fetch fails
                              addictionOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            )}
                            {/* Add option for custom entry if needed */}
                            <option value="custom">Add new...</option>
                          </Form.Control>
                          
                          {/* If "Add new..." is selected, show an input field */}
                          {row.historyOfAddiction === 'custom' && (
                            <Form.Control
                              type="text"
                              className="mt-1"
                              placeholder="Enter new addiction type"
                              onBlur={(e) => {
                                const value = e.target.value.trim();
                                if (value) {
                                  // Save the new option
                                  saveNewAddictionTypeOption(value).then(() => {
                                    // Update the current row with the new value
                                    handleTableChange('addictionTable', index, 'historyOfAddiction', value);
                                  });
                                }
                              }}
                            />
                          )}
                        </td>
                          <td>
                            <Form.Control
                              type="number"
                              min="0"
                              value={row.startedAtAge}
                              onChange={(e) => {
                                // Only allow integer values
                                const value = e.target.value.replace(/\D/g, '');
                                handleTableChange('addictionTable', index, 'startedAtAge', value);
                              }}
                              onKeyPress={(e) => {
                                // Allow only numbers (0-9)
                                if (!/[0-9]/.test(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              placeholder="Enter age"
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              min="0"
                              value={row.perDay}
                              onChange={(e) => {
                                // Only allow integer values
                                const value = e.target.value.replace(/\D/g, '');
                                handleTableChange('addictionTable', index, 'perDay', value);
                              }}
                              onKeyPress={(e) => {
                                // Allow only numbers (0-9)
                                if (!/[0-9]/.test(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              placeholder="Enter quantity"
                            />
                          </td>
                          <td>
                            <Form.Control
                              as="select"
                              value={row.duration}
                              onChange={(e) => handleTableChange('addictionTable', index, 'duration', e.target.value)}
                            >
                              <option value="">Select</option>
                              {fetchedDurationOptions.length > 0 ? (
                                fetchedDurationOptions.map((option, i) => (
                                  <option key={i} value={option}>{option}</option>
                                ))
                              ) : (
                                // Fallback to static options if API fetch fails
                                durationOptions.map((option, i) => (
                                  <option key={i} value={option}>{option}</option>
                                ))
                              )}
                            </Form.Control>
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              value={row.reformade}
                              onChange={(e) => handleTableChange('addictionTable', index, 'reformade', e.target.value)}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              value={row.comments}
                              onChange={(e) => handleTableChange('addictionTable', index, 'comments', e.target.value)}
                            />
                          </td>
                          <td>
                            {addictionTable.length > 1 && (
                              <span
                                className="delete-icon"
                                onClick={() => removeTableRow('addictionTable', index)}
                                style={{
                                  cursor: 'pointer',
                                  color: '#dc3545',
                                  fontSize: '18px',
                                  padding: '4px 8px',
                                  display: 'inline-block',
                                  width: '30px',
                                  textAlign: 'center'
                                }}
                              >
                                ×
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Form.Group>
          </div>
                
          <div className="history-section mb-3" style={{ paddingRight: '20px' }}>
            <Form.Group className="mb-3">
              <Form.Label className="history-title">History of Allergies : <span className="text-danger">*</span></Form.Label>
              <div className="d-flex align-items-center mb-2">
                <Form.Check
                  type="radio"
                  id="allergies-yes"
                  label="Yes"
                  name="allergiesHistory"
                  className="mr-3"
                  checked={allergiesHistory === 'Yes'}
                  onChange={() => handleRadioChange('allergiesHistory', 'Yes')}
                  style={radioStyle}
                />
                <Form.Check
                  type="radio"
                  id="allergies-no"
                  label="No History of Allergies"
                  name="allergiesHistory"
                  className="mx-3"
                  checked={allergiesHistory === 'No History of Allergies'}
                  onChange={() => handleRadioChange('allergiesHistory', 'No History of Allergies')}
                  style={radioStyle}
                />
                <Form.Check
                  type="radio"
                  id="allergies-dont-know"
                  label="Don't Know"
                  name="allergiesHistory"
                  checked={allergiesHistory === "Don't Know"}
                  onChange={() => handleRadioChange('allergiesHistory', "Don't Know")}
                  style={radioStyle}
                />
              </div>
              {errors.allergiesHistory && (
                <Form.Text className="text-danger">{errors.allergiesHistory}</Form.Text>
              )}
              
              {/* Allergies History Table - Show when Yes is selected */}
              {allergiesHistory === 'Yes' && (
                <div className="mt-3">
                  <table className="table table-bordered" style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                    <thead>
                      <tr>
                        <th style={tableHeadingStyle}>#</th>
                        <th style={tableHeadingStyle}>ALLERGY</th>
                        <th style={tableHeadingStyle}>ALLERGY TYPE</th>
                        <th style={tableHeadingStyle}>DURATION</th>
                        <th style={tableHeadingStyle}>COMMENT</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {allergiesTable.map((row, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            <Form.Control
                              as="select"
                              value={row.allergy}
                              onChange={(e) => {
                                const selectedValue = e.target.value;
                                
                                // First, update the current row with the selected value
                                const updatedTable = [...allergiesTable];
                                updatedTable[index] = {...updatedTable[index], allergy: selectedValue};
                                
                                // Then, if this is the last row and a value was selected, add a new row
                                if (index === allergiesTable.length - 1 && selectedValue.trim() !== '' && selectedValue !== 'custom') {
                                  updatedTable.push({ allergy: '', allergyType: '', duration: '', comment: '' });
                                }
                                
                                // Update the state once with all changes
                                setAllergiesTable(updatedTable);
                              }}
                            >
                              <option value="">Select allergy</option>
                              {fetchedAllergyOptions.length > 0 ? (
                                fetchedAllergyOptions.map((option, i) => (
                                  <option key={i} value={option}>{option}</option>
                                ))
                              ) : (
                                // Fallback to static options if API fetch fails
                                allergyOptions.map((option, i) => (
                                  <option key={i} value={option}>{option}</option>
                                ))
                              )}
                              {/* Add option for custom entry if needed */}
                              <option value="custom">Add new...</option>
                            </Form.Control>
                            
                            {/* If "Add new..." is selected, show an input field */}
                            {row.allergy === 'custom' && (
                              <Form.Control
                                type="text"
                                className="mt-1"
                                placeholder="Enter new allergy"
                                onBlur={(e) => {
                                  const value = e.target.value.trim();
                                  if (value) {
                                    // Save the new option
                                    saveNewAllergyOption(value).then(() => {
                                      // Update the current row with the new value
                                      handleTableChange('allergiesTable', index, 'allergy', value);
                                    });
                                  }
                                }}
                              />
                            )}
                          </td>
                          <td>
                            <Form.Control
                              as="select"
                              value={row.allergyType}
                              onChange={(e) => handleTableChange('allergiesTable', index, 'allergyType', e.target.value)}
                            >
                              <option value="">Select allergy type</option>
                              {fetchedAllergyTypeOptions.length > 0 ? (
                                fetchedAllergyTypeOptions.map((option, i) => (
                                  <option key={i} value={option}>{option}</option>
                                ))
                              ) : (
                                // Fallback to static options if API fetch fails
                                allergyTypeOptions.map((option, i) => (
                                  <option key={i} value={option}>{option}</option>
                                ))
                              )}
                              {/* Add option for custom entry if needed */}
                              <option value="custom">Add new...</option>
                            </Form.Control>
                            
                            {/* If "Add new..." is selected, show an input field */}
                            {row.allergyType === 'custom' && (
                              <Form.Control
                                type="text"
                                className="mt-1"
                                placeholder="Enter new allergy type"
                                onBlur={(e) => {
                                  const value = e.target.value.trim();
                                  if (value) {
                                    // Save the new option
                                    saveNewAllergyTypeOption(value).then(() => {
                                      // Update the current row with the new value
                                      handleTableChange('allergiesTable', index, 'allergyType', value);
                                    });
                                  }
                                }}
                              />
                            )}
                          </td>
                          <td>
                            <Form.Control
                              as="select"
                              value={row.duration}
                              onChange={(e) => handleTableChange('allergiesTable', index, 'duration', e.target.value)}
                            >
                              <option value="">Select</option>
                              {fetchedDurationOptions.length > 0 ? (
                                fetchedDurationOptions.map((option, i) => (
                                  <option key={i} value={option}>{option}</option>
                                ))
                              ) : (
                                // Fallback to static options if API fetch fails
                                durationOptions.map((option, i) => (
                                  <option key={i} value={option}>{option}</option>
                                ))
                              )}
                            </Form.Control>
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              value={row.comment}
                              onChange={(e) => handleTableChange('allergiesTable', index, 'comment', e.target.value)}
                            />
                          </td>
                          <td>
                            {allergiesTable.length > 1 && (
                              <span
                                className="delete-icon"
                                onClick={() => removeTableRow('allergiesTable', index)}
                                style={{
                                  cursor: 'pointer',
                                  color: '#dc3545',
                                  fontSize: '18px',
                                  padding: '4px 8px',
                                  display: 'inline-block',
                                  width: '30px',
                                  textAlign: 'center'
                                }}
                              >
                                ×
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Form.Group>
          </div>

          <div className="history-section mb-3" style={{ paddingRight: '20px' }}>
          <Form.Group className="mb-3">
            <Form.Label className="history-title">Family History : <span className="text-danger">*</span></Form.Label>
            <div className="d-flex align-items-center mb-2">
              <Form.Check
                type="radio"
                id="family-yes"
                label="Yes"
                name="familyHistory"
                className="mr-3"
                checked={familyHistory === 'Yes'}
                onChange={() => handleRadioChange('familyHistory', 'Yes')}
                style={radioStyle}
              />
              <Form.Check
                type="radio"
                id="family-no"
                label="No History of Family"
                name="familyHistory"
                className="mx-3"
                checked={familyHistory === 'No History of Family'}
                onChange={() => handleRadioChange('familyHistory', 'No History of Family')}
                style={radioStyle}
              />
              <Form.Check
                type="radio"
                id="family-dont-know"
                label="Don't Know"
                name="familyHistory"
                checked={familyHistory === "Don't Know"}
                onChange={() => handleRadioChange('familyHistory', "Don't Know")}
                style={radioStyle}
              />
            </div>
            {errors.familyHistory && (
              <Form.Text className="text-danger">{errors.familyHistory}</Form.Text>
            )}
            
            {/* Family History Table - Show when Yes is selected */}
            {familyHistory === 'Yes' && (
              <div className="mt-3">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th style={tableHeadingStyle}>#</th>
                      <th style={tableHeadingStyle}>HISTORY OF</th>
                      <th style={tableHeadingStyle}>TYPE OF RELATION</th>
                      <th style={tableHeadingStyle}>AT THE AGE OF (YEARS)</th>
                      <th style={tableHeadingStyle}>SPECIFICATION</th>
                      <th style={tableHeadingStyle}>COMMENT</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {familyTable.map((row, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>
                          <Form.Control
                            type="text"
                            value={row.historyOf}
                            onChange={(e) => {
                              const enteredValue = e.target.value;
                              
                              // First, update the current row with the entered value
                              const updatedTable = [...familyTable];
                              updatedTable[index] = {...updatedTable[index], historyOf: enteredValue};
                              
                              // Then, if this is the last row and a value was entered, add a new row
                              if (index === familyTable.length - 1 && enteredValue.trim() !== '') {
                                updatedTable.push({ historyOf: '', typeOfRelation: '', atTheAgeOf: '', specification: '', comment: '' });
                              }
                              
                              // Update the state once with all changes
                              setFamilyTable(updatedTable);
                            }}
                          />
                        </td>
                        <td>
                          <Form.Control
                            as="select"
                            value={row.typeOfRelation}
                            onChange={(e) => handleTableChange('familyTable', index, 'typeOfRelation', e.target.value)}
                          >
                            <option value="">Select relation</option>
                            {fetchedRelationOptions.length > 0 ? (
                              fetchedRelationOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            ) : (
                              // Fallback to static options if API fetch fails
                              relationTypeOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            )}
                            {/* Add option for custom entry if needed */}
                            <option value="custom">Add new...</option>
                          </Form.Control>
                          
                          {/* If "Add new..." is selected, show an input field */}
                          {row.typeOfRelation === 'custom' && (
                            <Form.Control
                              type="text"
                              className="mt-1"
                              placeholder="Enter new relation type"
                              onBlur={(e) => {
                                const value = e.target.value.trim();
                                if (value) {
                                  // Save the new option
                                  saveNewRelationOption(value).then(() => {
                                    // Update the current row with the new value
                                    handleTableChange('familyTable', index, 'typeOfRelation', value);
                                  });
                                }
                              }}
                            />
                          )}
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            min="0"
                            value={row.atTheAgeOf}
                            onChange={(e) => {
                              // Only allow integer values
                              const value = e.target.value.replace(/\D/g, '');
                              handleTableChange('familyTable', index, 'atTheAgeOf', value);
                            }}
                            onKeyPress={(e) => {
                              // Allow only numbers (0-9)
                              if (!/[0-9]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            placeholder="Enter age"
                          />
                        </td>
                        <td>
                          <Form.Control
                            as="select"
                            value={row.specification}
                            onChange={(e) => handleTableChange('familyTable', index, 'specification', e.target.value)}
                          >
                            <option value="">Select specification</option>
                            {fetchedSpecificationOptions.length > 0 ? (
                              fetchedSpecificationOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            ) : (
                              // Fallback to static options if API fetch fails
                              relationSpecificationOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            )}
                            {/* Add option for custom entry if needed */}
                            <option value="custom">Add new...</option>
                          </Form.Control>
                          
                          {/* If "Add new..." is selected, show an input field */}
                          {row.specification === 'custom' && (
                            <Form.Control
                              type="text"
                              className="mt-1"
                              placeholder="Enter new specification"
                              onBlur={(e) => {
                                const value = e.target.value.trim();
                                if (value) {
                                  // Save the new option
                                  saveNewSpecificationOption(value).then(() => {
                                    // Update the current row with the new value
                                    handleTableChange('familyTable', index, 'specification', value);
                                  });
                                }
                              }}
                            />
                          )}
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            value={row.comment}
                            onChange={(e) => handleTableChange('familyTable', index, 'comment', e.target.value)}
                          />
                        </td>
                        <td>
                          {familyTable.length > 1 && (
                            <span
                              className="delete-icon"
                              onClick={() => removeTableRow('familyTable', index)}
                              style={{
                                cursor: 'pointer',
                                color: '#dc3545',
                                fontSize: '18px',
                                padding: '4px 8px'
                              }}
                            >
                              ×
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Form.Group>
        </div>

        <div className="history-section mb-3" style={{ paddingRight: '20px' }}>
          <Form.Group className="mb-3">
            <Form.Label className="history-title">Current Medication : <span className="text-danger">*</span></Form.Label>
            <div className="d-flex align-items-center mb-2">
              <Form.Check
                type="radio"
                id="medication-yes"
                label="Yes"
                name="currentMedication"
                className="mr-3"
                checked={currentMedication === 'Yes'}
                onChange={() => handleRadioChange('currentMedication', 'Yes')}
                style={radioStyle}
              />
              <Form.Check
                type="radio"
                id="medication-no"
                label="No Current Medication"
                name="currentMedication"
                className="mx-3"
                checked={currentMedication === 'No Current Medication'}
                onChange={() => handleRadioChange('currentMedication', 'No Current Medication')}
                style={radioStyle}
              />
              <Form.Check
                type="radio"
                id="medication-dont-know"
                label="Don't Know"
                name="currentMedication"
                checked={currentMedication === "Don't Know"}
                onChange={() => handleRadioChange('currentMedication', "Don't Know")}
                style={radioStyle}
              />
            </div>
            {errors.currentMedication && (
              <Form.Text className="text-danger">{errors.currentMedication}</Form.Text>
            )}
            
            {/* Current Medication Table - Show when Yes is selected */}
            {currentMedication === 'Yes' && (
              <div className="mt-3">
                <table className="table table-bordered" style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                  <thead>
                    <tr>
                      <th style={tableHeadingStyle}>MEDICINE</th>
                      <th style={tableHeadingStyle}>DOSE</th>
                      <th style={tableHeadingStyle}>WHEN</th>
                      <th style={tableHeadingStyle}>DURATION</th>
                      <th style={tableHeadingStyle}>INSTRUCTIONS</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {medicationTable.map((row, index) => (
                      <tr key={index}>
                        <td>
                          <Form.Control
                            as="select"
                            value={row.medicine}
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              
                              // First, update the current row with the selected value
                              const updatedTable = [...medicationTable];
                              updatedTable[index] = {...updatedTable[index], medicine: selectedValue};
                              
                              // Then, if this is the last row and a value was selected, add a new row
                              if (index === medicationTable.length - 1 && selectedValue.trim() !== '' && selectedValue !== 'custom') {
                                updatedTable.push({ medicine: '', dose: '', when: '', duration: '', instructions: '' });
                              }
                              
                              // Update the state once with all changes
                              setMedicationTable(updatedTable);
                            }}
                          >
                            <option value="">Select medicine</option>
                            {fetchedMedicineOptions.length > 0 && 
                              fetchedMedicineOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            }
                            {/* Add option for custom entry if needed */}
                            <option value="custom">Add new...</option>
                          </Form.Control>
                          
                          {/* If "Add new..." is selected, show an input field */}
                          {row.medicine === 'custom' && (
                            <Form.Control
                              type="text"
                              className="mt-1"
                              placeholder="Enter new medicine"
                              onBlur={(e) => {
                                const value = e.target.value.trim();
                                if (value) {
                                  // Save the new option
                                  saveNewMedicineOption(value).then(() => {
                                    // Update the current row with the new value
                                    handleTableChange('medicationTable', index, 'medicine', value);
                                  });
                                }
                              }}
                            />
                          )}
                        </td>
                        <td>
                          <Form.Control
                            as="select"
                            value={row.dose}
                            onChange={(e) => handleTableChange('medicationTable', index, 'dose', e.target.value)}
                          >
                            <option value="">Select dose</option>
                            {fetchedDoseOptions.length > 0 ? (
                              fetchedDoseOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            ) : (
                              // Fallback to static options if API fetch fails
                              doseOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            )}
                            {/* Add option for custom entry if needed */}
                            <option value="custom">Add new...</option>
                          </Form.Control>
                          
                          {/* If "Add new..." is selected, show an input field */}
                          {row.dose === 'custom' && (
                            <Form.Control
                              type="text"
                              className="mt-1"
                              placeholder="Enter new dose"
                              onBlur={(e) => {
                                const value = e.target.value.trim();
                                if (value) {
                                  // Save the new option
                                  saveNewDoseOption(value).then(() => {
                                    // Update the current row with the new value
                                    handleTableChange('medicationTable', index, 'dose', value);
                                  });
                                }
                              }}
                            />
                          )}
                        </td>
                        <td>
                          <Form.Control
                            as="select"
                            value={row.when}
                            onChange={(e) => handleTableChange('medicationTable', index, 'when', e.target.value)}
                          >
                            <option value="">Select when</option>
                            {fetchedWhenOptions.length > 0 ? (
                              fetchedWhenOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            ) : (
                              // Fallback to static options if API fetch fails
                              whenOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            )}
                            {/* Add option for custom entry if needed */}
                            <option value="custom">Add new...</option>
                          </Form.Control>
                          
                          {/* If "Add new..." is selected, show an input field */}
                          {row.when === 'custom' && (
                            <Form.Control
                              type="text"
                              className="mt-1"
                              placeholder="Enter new timing"
                              onBlur={(e) => {
                                const value = e.target.value.trim();
                                if (value) {
                                  // Save the new option
                                  saveNewWhenOption(value).then(() => {
                                    // Update the current row with the new value
                                    handleTableChange('medicationTable', index, 'when', value);
                                  });
                                }
                              }}
                            />
                          )}
                        </td>
                        <td>
                          <Form.Control
                            as="select"
                            value={row.duration}
                            onChange={(e) => handleTableChange('medicationTable', index, 'duration', e.target.value)}
                          >
                            <option value="">Select</option>
                            {fetchedDurationOptions.length > 0 ? (
                              fetchedDurationOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            ) : (
                              // Fallback to static options if API fetch fails
                              durationOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            )}
                          </Form.Control>
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            value={row.instructions}
                            onChange={(e) => handleTableChange('medicationTable', index, 'instructions', e.target.value)}
                          />
                        </td>
                        <td>
                          {medicationTable.length > 1 && (
                            <span
                              className="delete-icon"
                              onClick={() => removeTableRow('medicationTable', index)}
                              style={{
                                cursor: 'pointer',
                                color: '#dc3545',
                                fontSize: '18px',
                                padding: '4px 8px'
                              }}
                            >
                              ×
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Form.Group>
        </div>

          <div className="history-section mb-3" style={{ paddingRight: '20px' }}>
            <Form.Group className="mb-3">
              <Form.Label className="history-title">Past Medical History : <span className="text-danger">*</span></Form.Label>
              <div className="d-flex align-items-center mb-2">
                <Form.Check
                  type="radio"
                  id="past-medical-yes"
                  label="Yes"
                  name="pastMedicalHistory"
                  className="mr-3"
                  checked={pastMedicalHistory === 'Yes'}
                  onChange={() => handleRadioChange('pastMedicalHistory', 'Yes')}
                  style={radioStyle}
                />
                <Form.Check
                  type="radio"
                  id="past-medical-no"
                  label="No Past Medical History"
                  name="pastMedicalHistory"
                  className="mx-3"
                  checked={pastMedicalHistory === 'No Past Medical History'}
                  onChange={() => handleRadioChange('pastMedicalHistory', 'No Past Medical History')}
                  style={radioStyle}
                />
                <Form.Check
                  type="radio"
                  id="past-medical-dont-know"
                  label="Don't Know"
                  name="pastMedicalHistory"
                  checked={pastMedicalHistory === "Don't Know"}
                  onChange={() => handleRadioChange('pastMedicalHistory', "Don't Know")}
                  style={radioStyle}
                />
              </div>
              {errors.pastMedicalHistory && (
                <Form.Text className="text-danger">{errors.pastMedicalHistory}</Form.Text>
              )}
              
              {/* Past Medical History Text Field - Show when Yes is selected */}
              {pastMedicalHistory === 'Yes' && (
                <div className="mt-3">
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Enter past medical history details"
                    value={pastMedicalHistoryDetails || ''}
                    onChange={(e) => setPastMedicalHistoryDetails(e.target.value)}
                    style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}
                    isInvalid={!!errors.pastMedicalHistoryDetails}
                  />
                  {errors.pastMedicalHistoryDetails && (
                    <Form.Text className="text-danger" style={{ marginLeft: '20px' }}>{errors.pastMedicalHistoryDetails}</Form.Text>
                  )}
                </div>
              )}
            </Form.Group>
          </div>

          <div className="history-section mb-3" style={{ paddingRight: '20px' }}>
          <Form.Group className="mb-3">
            <Form.Label className="history-title">Comorbidities : <span className="text-danger">*</span></Form.Label>
            <div className="d-flex align-items-center mb-2">
              <Form.Check
                type="radio"
                id="comorbidities-yes"
                label="Yes"
                name="comorbidities"
                className="mr-3"
                checked={comorbidities === 'Yes'}
                onChange={() => handleRadioChange('comorbidities', 'Yes')}
                style={radioStyle}
              />
              <Form.Check
                type="radio"
                id="comorbidities-no"
                label="No History of Comorbidities"
                name="comorbidities"
                className="mx-3"
                checked={comorbidities === 'No History of Comorbidities'}
                onChange={() => handleRadioChange('comorbidities', 'No History of Comorbidities')}
                style={radioStyle}
              />
              <Form.Check
                type="radio"
                id="comorbidities-dont-know"
                label="Don't Know"
                name="comorbidities"
                checked={comorbidities === "Don't Know"}
                onChange={() => handleRadioChange('comorbidities', "Don't Know")}
                style={radioStyle}
              />
            </div>
            {errors.comorbidities && (
              <Form.Text className="text-danger">{errors.comorbidities}</Form.Text>
            )}
            
            {/* Comorbidities Table - Show when Yes is selected */}
            {comorbidities === 'Yes' && (
              <div className="mt-3">
                <table className="table table-bordered" style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}>
                  <thead>
                    <tr>
                      <th style={tableHeadingStyle}>#</th>
                      <th style={tableHeadingStyle}>COMORBIDITY</th>
                      <th style={tableHeadingStyle}>DURATION</th>
                      <th style={tableHeadingStyle}>ON MEDICATION</th>
                      <th style={tableHeadingStyle}>COMMENT</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {comorbidityTable.map((row, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>
                          <Form.Control
                            as="select"
                            value={row.comorbidity}
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              
                              // First, update the current row with the selected value
                              const updatedTable = [...comorbidityTable];
                              updatedTable[index] = {...updatedTable[index], comorbidity: selectedValue};
                              
                              // Then, if this is the last row and a value was selected, add a new row
                              if (index === comorbidityTable.length - 1 && selectedValue.trim() !== '' && selectedValue !== 'custom') {
                                updatedTable.push({ comorbidity: '', duration: '', onMedication: '', comment: '' });
                              }
                              
                              // Update the state once with all changes
                              setComorbidityTable(updatedTable);
                            }}
                          >
                            <option value="">Select comorbidity</option>
                            {fetchedComorbidityOptions.length > 0 ? (
                              fetchedComorbidityOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            ) : (
                              // Fallback to static options if API fetch fails
                              comorbiditiesOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            )}
                            {/* Add option for custom entry if needed */}
                            <option value="custom">Add new...</option>
                          </Form.Control>
                          
                          {/* If "Add new..." is selected, show an input field */}
                          {row.comorbidity === 'custom' && (
                            <Form.Control
                              type="text"
                              className="mt-1"
                              placeholder="Enter new comorbidity"
                              onBlur={(e) => {
                                const value = e.target.value.trim();
                                if (value) {
                                  // Save the new option
                                  saveNewComorbidityOption(value).then(() => {
                                    // Update the current row with the new value
                                    handleTableChange('comorbidityTable', index, 'comorbidity', value);
                                  });
                                }
                              }}
                            />
                          )}
                        </td>
                        <td>
                          <Form.Control
                            as="select"
                            value={row.duration}
                            onChange={(e) => handleTableChange('comorbidityTable', index, 'duration', e.target.value)}
                          >
                            <option value="">Select</option>
                            {fetchedDurationOptions.length > 0 ? (
                              fetchedDurationOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            ) : (
                              // Fallback to static options if API fetch fails
                              durationOptions.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))
                            )}
                          </Form.Control>
                        </td>
                        <td>
                          <div className="d-flex justify-content-center">
                            <Form.Check 
                              type="radio"
                              id={`on-medication-no-${index}`}
                              label="No"
                              name={`onMedication-${index}`}
                              className="mr-3"
                              checked={row.onMedication === 'No'}
                              onChange={() => handleTableChange('comorbidityTable', index, 'onMedication', 'No')}
                              style={radioStyle}
                            />
                            <Form.Check 
                              type="radio"
                              id={`on-medication-yes-${index}`}
                              label="Yes"
                              name={`onMedication-${index}`}
                              checked={row.onMedication === 'Yes'}
                              onChange={() => handleTableChange('comorbidityTable', index, 'onMedication', 'Yes')}
                              style={radioStyle}
                            />
                          </div>
                        </td>
                        <td>
                          <Form.Control 
                            type="text" 
                            value={row.comment}
                            onChange={(e) => handleTableChange('comorbidityTable', index, 'comment', e.target.value)}
                          />
                        </td>
                        <td>
                          {comorbidityTable.length > 1 && (
                            <span
                              className="delete-icon"
                              onClick={() => removeTableRow('comorbidityTable', index)}
                              style={{
                                cursor: 'pointer',
                                color: '#dc3545',
                                fontSize: '18px',
                                padding: '4px 8px'
                              }}
                            >
                              ×
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Form.Group>
        </div>

          {/* Past Surgical History Section */}
          <div className="history-section mb-3" style={{ paddingRight: '20px' }}>
            <Form.Group className="mb-3">
                <Form.Label className="history-title">Past Surgical History : <span className="text-danger">*</span></Form.Label>
                <div className="d-flex align-items-center mb-2">
                <Form.Check
                    type="radio"
                    id="past-surgical-yes"
                    label="Yes"
                    name="pastSurgicalHistory"
                    className="mr-3"
                    checked={pastSurgicalHistory === 'Yes'}
                    onChange={() => handleRadioChange('pastSurgicalHistory', 'Yes')}
                    style={radioStyle}
                />
                <Form.Check
                    type="radio"
                    id="past-surgical-no"
                    label="No Past Surgical History"
                    name="pastSurgicalHistory"
                    className="mx-3"
                    checked={pastSurgicalHistory === 'No Past Surgical History'}
                    onChange={() => handleRadioChange('pastSurgicalHistory', 'No Past Surgical History')}
                    style={radioStyle}
                />
                <Form.Check
                    type="radio"
                    id="past-surgical-dont-know"
                    label="Don't Know"
                    name="pastSurgicalHistory"
                    checked={pastSurgicalHistory === "Don't Know"}
                    onChange={() => handleRadioChange('pastSurgicalHistory', "Don't Know")}
                    style={radioStyle}
                />
                </div>
                {errors.pastSurgicalHistory && (
                <Form.Text className="text-danger">{errors.pastSurgicalHistory}</Form.Text>
                )}
                
                {/* Past Surgical History Text Field - Show when Yes is selected */}
                {pastSurgicalHistory === 'Yes' && (
                <div className="mt-3">
                    <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter past surgical history details"
                    value={pastSurgicalHistoryDetails || ''}
                    onChange={(e) => setPastSurgicalHistoryDetails(e.target.value)}
                    style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}
                    isInvalid={!!errors.pastSurgicalHistoryDetails}
                    />
                    {errors.pastSurgicalHistoryDetails && (
                      <Form.Text className="text-danger" style={{ marginLeft: '20px' }}>{errors.pastSurgicalHistoryDetails}</Form.Text>
                    )}
                </div>
                )}
            </Form.Group>
          </div>

          {/* Reproductive History Section - Show different fields based on gender */}
          <div 
            className="history-section mb-3 p-3" 
            style={{
                border: '1px solid #dee2e6', 
                borderRadius: '4px'
            }}
          >
            <Form.Label className="history-title">Reproductive History</Form.Label>
            
            {/* Female-specific fields */}
            {patientGender === 'Female' && (
              <>
                <Row>
                  <Col>
                    <Form.Group>
                      <Form.Label>Menarche Age</Form.Label>
                      <Form.Control
                        type="text"
                        name="menarche"
                        value={menarche}
                        onChange={(e) => handleChange(e)}
                        placeholder="Enter age"
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Label>Periods</Form.Label>
                    <div className="d-flex">
                      <Form.Check
                        type="radio"
                        id="periods-regular"
                        label="Regular"
                        name="periods"
                        className="mr-3"
                        checked={periods === 'Regular'}
                        onChange={() => handleRadioChange('periods', 'Regular')}
                        style={radioStyle}
                      />
                      <Form.Check
                        type="radio"
                        id="periods-irregular"
                        label="Irregular"
                        name="periods"
                        checked={periods === 'Irregular'}
                        onChange={() => handleRadioChange('periods', 'Irregular')}
                        style={radioStyle}
                      />
                    </div>
                  </Col>
                  <Col>
                    <Form.Label>Bleeding</Form.Label>
                    <div className="d-flex">
                      <Form.Check
                        type="radio"
                        id="bleeding-less"
                        label="Less"
                        name="bleeding"
                        className="mr-3"
                        checked={bleeding === 'Less'}
                        onChange={() => handleRadioChange('bleeding', 'Less')}
                        style={radioStyle}
                      />
                      <Form.Check
                        type="radio"
                        id="bleeding-normal"
                        label="Normal"
                        name="bleeding"
                        className="mr-3"
                        checked={bleeding === 'Normal'}
                        onChange={() => handleRadioChange('bleeding', 'Normal')}
                        style={radioStyle}
                      />
                      <Form.Check
                        type="radio"
                        id="bleeding-excessive"
                        label="Excessive"
                        name="bleeding"
                        checked={bleeding === 'Excessive'}
                        onChange={() => handleRadioChange('bleeding', 'Excessive')}
                        style={radioStyle}
                      />
                    </div>
                  </Col>
                </Row>
                
                <Row className="mt-3">
                  <Col>
                    <Form.Label>Menopausal Status</Form.Label>
                    <div className="d-flex">
                      <Form.Check
                        type="radio"
                        id="menopausal-pre"
                        label="Pre"
                        name="menopausalStatus"
                        className="mr-3"
                        checked={menopausalStatus === 'Pre'}
                        onChange={() => handleRadioChange('menopausalStatus', 'Pre')}
                        style={radioStyle}
                      />
                      <Form.Check
                        type="radio"
                        id="menopausal-post"
                        label="Post"
                        name="menopausalStatus"
                        className="mr-3"
                        checked={menopausalStatus === 'Post'}
                        onChange={() => handleRadioChange('menopausalStatus', 'Post')}
                        style={radioStyle}
                      />
                      <Form.Check
                        type="radio"
                        id="menopausal-peri"
                        label="Peri"
                        name="menopausalStatus"
                        checked={menopausalStatus === 'Peri'}
                        onChange={() => handleRadioChange('menopausalStatus', 'Peri')}
                        style={radioStyle}
                      />
                    </div>
                  </Col>
                  <Col>
                    <Form.Label>Marital Status</Form.Label>
                    <div className="d-flex">
                      <Form.Check
                        type="radio"
                        id="marital-married"
                        label="Married"
                        name="maritalStatus"
                        className="mr-3"
                        checked={maritalStatus === 'Married'}
                        onChange={() => handleRadioChange('maritalStatus', 'Married')}
                        style={radioStyle}
                      />
                      <Form.Check
                        type="radio"
                        id="marital-unmarried"
                        label="Unmarried"
                        name="maritalStatus"
                        checked={maritalStatus === 'Unmarried'}
                        onChange={() => handleRadioChange('maritalStatus', 'Unmarried')}
                        style={radioStyle}
                      />
                    </div>
                  </Col>
                  <Col>
                    <Form.Label>Children</Form.Label>
                    <div className="d-flex">
                      <Form.Check
                        type="radio"
                        id="children-no"
                        label="No"
                        name="children"
                        className="mr-3"
                        checked={children === 'No'}
                        onChange={() => handleRadioChange('children', 'No')}
                        style={radioStyle}
                      />
                      <Form.Check
                        type="radio"
                        id="children-yes"
                        label="Yes"
                        name="children"
                        checked={children === 'Yes'}
                        onChange={() => handleRadioChange('children', 'Yes')}
                        style={radioStyle}
                      />
                    </div>
                  </Col>
                </Row>

                {/* Additional Children Details for Female - Show when Children is Yes */}
                {children === 'Yes' && (
                  <Row className="mt-3">
                    <Col>
                      <Form.Label>No of Children</Form.Label>
                      <Form.Control
                        type="text"
                        name="numberOfChildren"
                        value={numberOfChildren}
                        onChange={(e) => handleChange(e)}
                        placeholder="Enter number of children"
                      />
                    </Col>
                    <Col>
                      <Form.Label>Age at 1st Birth</Form.Label>
                      <Form.Control
                        type="text"
                        name="ageAtFirstBirth"
                        value={ageAtFirstBirth}
                        onChange={(e) => handleChange(e)}
                        placeholder="Enter age"
                      />
                    </Col>
                    <Col>
                      <Form.Label>Breast Feed</Form.Label>
                      <div className="d-flex">
                        <Form.Check
                          type="radio"
                          id="breastfeed-no"
                          label="No"
                          name="breastFeed"
                          className="mr-3"
                          checked={breastFeed === 'No'}
                          onChange={() => handleRadioChange('breastFeed', 'No')}
                          style={radioStyle}
                        />
                        <Form.Check
                          type="radio"
                          id="breastfeed-yes"
                          label="Yes"
                          name="breastFeed"
                          checked={breastFeed === 'Yes'}
                          onChange={() => handleRadioChange('breastFeed', 'Yes')}
                          style={radioStyle}
                        />
                      </div>
                    </Col>
                  </Row>
                )}
              </>
            )}

            {/* Male-specific fields */}
            {patientGender === 'Male' && (
              <>
                <Row>
                  <Col>
                    <Form.Label>Marital Status</Form.Label>
                    <div className="d-flex">
                      <Form.Check
                        type="radio"
                        id="marital-married-male"
                        label="Married"
                        name="maritalStatus"
                        className="mr-3"
                        checked={maritalStatus === 'Married'}
                        onChange={() => handleRadioChange('maritalStatus', 'Married')}
                        style={radioStyle}
                      />
                      <Form.Check
                        type="radio"
                        id="marital-unmarried-male"
                        label="Unmarried"
                        name="maritalStatus"
                        checked={maritalStatus === 'Unmarried'}
                        onChange={() => handleRadioChange('maritalStatus', 'Unmarried')}
                        style={radioStyle}
                      />
                    </div>
                  </Col>
                  <Col>
                    <Form.Label>Children</Form.Label>
                    <div className="d-flex">
                      <Form.Check
                        type="radio"
                        id="children-no-male"
                        label="No"
                        name="children"
                        className="mr-3"
                        checked={children === 'No'}
                        onChange={() => handleRadioChange('children', 'No')}
                        style={radioStyle}
                      />
                      <Form.Check
                        type="radio"
                        id="children-yes-male"
                        label="Yes"
                        name="children"
                        checked={children === 'Yes'}
                        onChange={() => handleRadioChange('children', 'Yes')}
                        style={radioStyle}
                      />
                    </div>
                  </Col>
                  <Col>
                    {/* Empty column for spacing */}
                  </Col>
                </Row>

                {/* Additional Children Details for Male - Show when Children is Yes */}
                {children === 'Yes' && (
                  <Row className="mt-3">
                    <Col>
                      <Form.Label>No of Children</Form.Label>
                      <Form.Control
                        type="text"
                        name="numberOfChildren"
                        value={numberOfChildren}
                        onChange={(e) => handleChange(e)}
                        placeholder="Enter number of children"
                      />
                    </Col>
                    <Col>
                      {/* Empty column for spacing */}
                    </Col>
                    <Col>
                      {/* Empty column for spacing */}
                    </Col>
                  </Row>
                )}
              </>
            )}
          </div>

          {/* Past Oncology History Section */}
          <div className="history-section mb-3" style={{ paddingRight: '20px' }}>
            <Form.Group className="mb-3">
                <Form.Label className="history-title">Past Oncology History : <span className="text-danger">*</span></Form.Label>
                <div className="d-flex align-items-center mb-2">
                <Form.Check
                    type="radio"
                    id="past-oncology-yes"
                    label="Yes"
                    name="pastOncologyHistory"
                    className="mr-3"
                    checked={pastOncologyHistory === 'Yes'}
                    onChange={() => handleRadioChange('pastOncologyHistory', 'Yes')}
                    style={radioStyle}
                />
                <Form.Check
                    type="radio"
                    id="past-oncology-no"
                    label="No Past Oncology History"
                    name="pastOncologyHistory"
                    className="mx-3"
                    checked={pastOncologyHistory === 'No Past Oncology History'}
                    onChange={() => handleRadioChange('pastOncologyHistory', 'No Past Oncology History')}
                    style={radioStyle}
                />
                <Form.Check
                    type="radio"
                    id="past-oncology-dont-know"
                    label="Don't Know"
                    name="pastOncologyHistory"
                    checked={pastOncologyHistory === "Don't Know"}
                    onChange={() => handleRadioChange('pastOncologyHistory', "Don't Know")}
                    style={radioStyle}
                />
                </div>
                {errors.pastOncologyHistory && (
                <Form.Text className="text-danger">{errors.pastOncologyHistory}</Form.Text>
                )}
                
                {/* Past Oncology History Text Field - Show when Yes is selected */}
                {pastOncologyHistory === 'Yes' && (
                <div className="mt-3">
                    <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter past oncology history details"
                    value={pastOncologyHistoryDetails || ''}
                    onChange={(e) => setPastOncologyHistoryDetails(e.target.value)}
                    style={{ width: 'calc(100% - 80px)', marginLeft: '20px', marginRight: '20px' }}
                    isInvalid={!!errors.pastOncologyHistoryDetails}
                    />
                    {errors.pastOncologyHistoryDetails && (
                      <Form.Text className="text-danger" style={{ marginLeft: '20px' }}>{errors.pastOncologyHistoryDetails}</Form.Text>
                    )}
                </div>
                )}
            </Form.Group>
          </div>

          <div className="form-buttons mt-4 mb-5 text-center" style={{ paddingRight: '20px' }}>
            <Button type="button" variant="outline-danger" className="mr-1" onClick={toggleEditForm}>
              Cancel
            </Button>
            <Button type="submit" variant="secondary" className="mr-3" disabled={saveLoader}>
              {saveLoader ? (
                <>
                  <Spinner animation="border" size="sm" className="mr-2" />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </div>
        </Form>
      </Container>
    );
  };

  // This is a placeholder - the actual renderPatientInfo function is defined below

  // Function to render patient information header
  const renderPatientInfo = () => {
    try {
      console.log('Rendering patient info with data:', patientData);
      
      // Prepare formatted values with fallbacks
      const patientName = patientData && (patientData.firstName || patientData.lastName) ? 
        `${patientData.firstName || ''} ${patientData.lastName || ''}`.trim() : '';
      
      // Format age with 'Years' suffix if available
      let ageDisplay = '';
      if (patientData && patientData.age) {
        // Convert to number and check if it's valid
        const ageNum = Number(patientData.age);
        if (!isNaN(ageNum)) {
          ageDisplay = `${ageNum} Years`;
        } else {
          ageDisplay = patientData.age; // Use as-is if not a number
        }
      }
      
      // Get gender from either patientData or the separate patientGender state
      const genderDisplay = patientData?.gender || patientGender || '';
      
      // Use caseNo if available, otherwise fall back to patientId
      const caseNoDisplay = patientData?.caseNo || patientId || '';
      
      console.log('Formatted values for display:', {
        caseNo: caseNoDisplay,
        patientName,
        ageDisplay,
        genderDisplay
      });
      
      return (
        <Row className="g-4 mb-4">
          <Col lg="3" className="form-inline">
            <Form.Group className="mb-0">
              <Form.Label className="mr-3">Case No.</Form.Label>
              <Form.Control
                type="text"
                className="readOnlyView inline"
                value={caseNoDisplay}
                name="caseNo"
                readOnly
              />
            </Form.Group>
          </Col>
          <Col lg="3" className="form-inline">
            <Form.Group className="mb-0">
              <Form.Label className="mr-3">Patient Name</Form.Label>
              <Form.Control
                type="text"
                className="readOnlyView inline"
                value={patientName}
                name="patientName"
                readOnly
              />
            </Form.Group>
          </Col>
          <Col lg="3" className="form-inline">
            <Form.Group className="mb-0">
              <Form.Label className="mr-3">Age</Form.Label>
              <Form.Control
                type="text"
                className="readOnlyView inline"
                value={ageDisplay}
                name="patientAge"
                readOnly
              />
            </Form.Group>
          </Col>
          <Col lg="3" className="form-inline">
            <Form.Group className="mb-0">
              <Form.Label className="mr-3">Gender</Form.Label>
              <Form.Control
                type="text"
                className="readOnlyView inline"
                value={genderDisplay}
                name="gender"
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>
      );
    } catch (error) {
      console.error('Error rendering patient info:', error);
      return null;
    }
  };

  // Main render return
  return (
    <div className="medical-history-component" style={{ marginRight: '20px', marginLeft: '20px', paddingBottom: '50px' }}>
      {showEditForm ? renderEditForm() : renderMedicalHistoryView()}
    </div>
  );
};

export default MedicalHistory;